import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import API_BASE_URL from "./config/api";
import { useFeedback } from "./context/FeedbackContext";
import Footer from "./components/Footer";
import "./AITripDetailPage.css";

function AITripDetailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const itinerary = location.state?.itinerary;
  const [hotelDetails, setHotelDetails] = useState(null);
  const [cabDetails, setCabDetails] = useState(null);
  const [destinationDetails, setDestinationDetails] = useState([]);
  const [activityNames, setActivityNames] = useState({}); // Kept for backward compatibility if needed
  const [activityDetails, setActivityDetails] = useState({}); // New state for full details
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFormModalOpen, setFormModalOpen] = useState(false);
  const [isBreakdownModalOpen, setIsBreakdownModalOpen] = useState(false);
  const [breakdownData, setBreakdownData] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    selectedPackage: itinerary?._id || "",
  });
  const [errors, setErrors] = useState({ email: '', phone: '' });
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [isChangeModalOpen, setChangeModalOpen] = useState(false);
  const [changeModalType, setChangeModalType] = useState("");
  const [changeModalData, setChangeModalData] = useState(null);
  const [changeModalDay, setChangeModalDay] = useState(null);
  const [changeModalIndex, setChangeModalIndex] = useState(null);
  const [alternativeOptions, setAlternativeOptions] = useState([]);
  const [loadingAlternatives, setLoadingAlternatives] = useState(false);
  const [packageDetails, setPackageDetails] = useState(null);
  const [modifiedItinerary, setModifiedItinerary] = useState(null);
  const [expandedActivities, setExpandedActivities] = useState({});
  const [promoCode, setPromoCode] = useState('');
  const [discountedTotal, setDiscountedTotal] = useState(0);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountApplied, setDiscountApplied] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [promocodes, setPromocodes] = useState([]);
  const [promoFetchError, setPromoFetchError] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showPromocodes, setShowPromocodes] = useState(false);
  const [isContactCaptureOpen, setIsContactCaptureOpen] = useState(false);
  const [contactAction, setContactAction] = useState(''); // 'save', 'talk', 'book'
  const [contactForm, setContactForm] = useState({ name: '', mobile: '', email: '' });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [contactSubmitting, setContactSubmitting] = useState(false);

  // UI Locking & CTA State
  const [isPlanUnlocked, setIsPlanUnlocked] = useState(false);
  const [isCustomizeVisible, setIsCustomizeVisible] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockEmail, setUnlockEmail] = useState("");
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [showUnlockToast, setShowUnlockToast] = useState(false);
  const [showFloatingCTA, setShowFloatingCTA] = useState(false);

  // Share With Partner State
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareForm, setShareForm] = useState({ name: '', email: '' });
  const [shareSubmitting, setShareSubmitting] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);

  // Refinement State
  const [customTripType, setCustomTripType] = useState("Comfort");
  const [customHotelRating, setCustomHotelRating] = useState([4]);
  const [customMealPlan, setCustomMealPlan] = useState("CPAI");
  const [customIntent, setCustomIntent] = useState("Mix of both");
  const [customStayPriority, setCustomStayPriority] = useState("Premium stays");
  const [customTripPace, setCustomTripPace] = useState("Balanced (steady)");
  const [customGoal, setCustomGoal] = useState("Memorable experiences");
  const [customAdults, setCustomAdults] = useState(2);
  const [customChildren, setCustomChildren] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSticky, setIsSticky] = useState(false);

  // Rich Loading State for Refresh
  const [loadingMessage, setLoadingMessage] = useState("Initiating AI Trip Planner...");
  const [currentFactIndex, setCurrentFactIndex] = useState(0);

  const funFacts = [
    "India has the highest cricket ground in the world.",
    "The Kumbh Mela is visible from space.",
    "India is the wettest inhabited place on Earth.",
    "Rajasthan has a temple of rats.",
    "The value of Pi was first calculated in India.",
    "Yoga originated in India over 5,000 years ago.",
    "India produces the most spices in the world.",
    "The Taj Mahal changes color depending on the time of day.",
    "Varanasi is one of the oldest continuously inhabited cities in existence.",
    "India has a floating post office in Dal Lake, Srinagar."
  ];

  useEffect(() => {
    let interval;
    if (isRefreshing) {
      interval = setInterval(() => {
        setCurrentFactIndex((prev) => (prev + 1) % funFacts.length);
      }, 4000);
    }
    return () => clearInterval(interval);
  }, [isRefreshing]);

  // Feedback hook
  const { showItineraryFeedback, showBookingFeedback } = useFeedback();

  const EMAIL_API_URL = `${API_BASE_URL}/send-package-details`;

  // Floating CTA scroll listener
  useEffect(() => {
    const handleFloatingScroll = () => {
      const regretSection = document.querySelector('.regret-minimization-section');
      if (regretSection) {
        const rect = regretSection.getBoundingClientRect();
        setShowFloatingCTA(rect.bottom < 0);
      }
    };
    window.addEventListener('scroll', handleFloatingScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleFloatingScroll);
  }, []);

  // Update refinement state when packageDetails loads
  useEffect(() => {
    if (packageDetails) {
      const rating = packageDetails.hotelRating?.[0] || 4;
      setCustomHotelRating([rating]);
      setCustomTripType(rating === 5 ? "Luxury" : rating === 3 ? "Budget" : "Comfort");
      setCustomMealPlan(packageDetails.mealPlan || "CPAI");
      setCustomIntent(packageDetails.intent || "Mix of both");
      setCustomStayPriority(packageDetails.stay_priority || "Premium stays");
      setCustomTripPace(packageDetails.pace || "Balanced (steady)");
      setCustomGoal(packageDetails.goal || "Memorable experiences");

      if (packageDetails.guests && packageDetails.guests.length > 0) {
        setCustomAdults(packageDetails.guests[0].adults || 2);
        setCustomChildren(packageDetails.guests[0].children?.length || 0);
      }
    }
  }, [packageDetails]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 600) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleRefreshItinerary = async () => {
    if (!packageDetails) return;
    setIsRefreshing(true);
    setError(null);

    setLoadingMessage("Regenerating your perfect itinerary...");
    const messages = ["Searching for best hotels...", "Checking cab availability...", "Curating activities...", "Finalizing itinerary..."];
    let msgIndex = 0;
    const messageInterval = setInterval(() => { 
      if (msgIndex < messages.length) { 
        setLoadingMessage(messages[msgIndex]); 
        msgIndex++; 
      } 
    }, 2500);

    const formatDateToLocal = (dateStr) => {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const requestBody = {
      stateName: packageDetails.state || "Kerala",
      destinations: destinationDetails.map(d => (d.name || "").trim()),
      startDate: formatDateToLocal(packageDetails.startDate),
      endDate: formatDateToLocal(packageDetails.endDate),
      duration: packageDetails.duration || (modifiedItinerary?.itinerary?.length || 0),
      activities: [],
      itinerarySequence: [],
      hotelRating: customHotelRating,
      amenities: packageDetails.amenities || ["Free Wi-Fi", "Parking"],
      roomCategory: packageDetails.roomCategory || "Standard room",
      cabType: packageDetails.cabType || "Sedan",
      description: packageDetails.description,
      coverImage: packageDetails.coverImage,
      mealPlan: customMealPlan,
      guests: [{ adults: customAdults, children: Array(customChildren).fill(10) }],
      intent: customIntent,
      stay_priority: customStayPriority,
      pace: customTripPace,
      goal: customGoal,
      tripType: customTripType, // Bug fix: was missing, causing couplePreferences.tripType to be null
    };

    try {
      const response = await fetch(`${API_BASE_URL}/ai-itinerary/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Failed to refresh itinerary");

      // Update state in-place instead of navigate + reload
      // This ensures price, "Why this plan works", and all sections update immediately
      setModifiedItinerary({ ...result });
      setFormData(prev => ({ ...prev, selectedPackage: result._id }));
      setBreakdownData(null); // Reset breakdown so it recalculates from new data
      setDiscountApplied(false);
      setPromoCode('');
      setPromoCode('');
      setDiscountAmount(0);
      setDiscountPercentage(0);

      // Immediately update packageDetails so "Why this plan works" updates instantly
      setPackageDetails(result);

      // Fetch all details for the new itinerary (hotels, cabs, destinations, activities, package)
      // Pass true for isBackgroundFetch so it doesn't trigger the full-screen spinner again
      await fetchAllDetailsForItinerary(result, true);

      // Scroll to top to see the refreshed itinerary
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error("Refresh failed:", err);
      setError("Failed to update itinerary. Please try again.");
    } finally {
      clearInterval(messageInterval);
      setIsRefreshing(false);
    }
  };

  // Styles moved to AITripDetailPage.css

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (itinerary) {
      setModifiedItinerary({ ...itinerary });
      setFormData(prev => ({ ...prev, selectedPackage: itinerary._id }));
    }
  }, [itinerary]);

  // Removed: Auto feedback popup after itinerary generation
  // Users can still trigger feedback via the manual feedback button or the in-page link

  useEffect(() => {
    const fetchPromocodes = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/promocodes`);
        if (!response.ok) throw new Error("Failed to fetch promocodes");
        const result = await response.json();
        // Only show active promocodes to users
        const activePromocodes = result.filter(promo => promo.isActive === true);
        setPromocodes(activePromocodes);
      } catch (error) {
        console.error("Error fetching promocodes:", error);
        setPromoFetchError(error.message);
      }
    };
    fetchPromocodes();
  }, []);

  // Reusable function to fetch all related details for a given itinerary object
  const fetchAllDetailsForItinerary = async (itineraryObj, isBackgroundFetch = false) => {
    if (!itineraryObj || !itineraryObj.itinerary) return;
    if (!isBackgroundFetch) setLoading(true);
    setError(null);
    try {
      const hotelIds = [...new Set(itineraryObj.itinerary.flatMap(day => day.hotels?.map(h => h.hotelId) || []))];
      const cabIds = [...new Set(itineraryObj.itinerary.flatMap(day => day.cabs?.map(c => c.cabId)))];
      const destinationIds = [...new Set(itineraryObj.itinerary.flatMap(day => day.destinations?.map(d => d.destinationId)))];
      const activityIds = [...new Set(itineraryObj.itinerary.flatMap(day => day.destinations?.flatMap(dest => dest.activities?.map(a => a.activityId))))];
      const [hotelResponses, cabResponses, destinationResponses, activityResponses, packageResponse] = await Promise.all([
        Promise.all(hotelIds.map(id => fetch(`${API_BASE_URL}/hotels/${id}`).then(res => res.json()))),
        Promise.all(cabIds.map(id => fetch(`${API_BASE_URL}/cabs/${id}`).then(res => res.json()))),
        Promise.all(destinationIds.map(id => fetch(`${API_BASE_URL}/destinations/${id}`).then(res => res.json()))),
        Promise.all(activityIds.map(id =>
          fetch(`${API_BASE_URL}/activities/${id}`)
            .then(res => res.ok ? res.json() : null)
            .catch(() => null)
        )),
        fetch(`${API_BASE_URL}/packages/${itineraryObj._id}`).then(res => res.json()),
      ]);
      setHotelDetails(hotelResponses);
      setCabDetails(cabResponses);
      setDestinationDetails(destinationResponses);

      const activityMap = {};
      const nameMap = {};
      activityResponses.forEach(activity => {
        if (activity) {
          activityMap[activity._id] = activity;
          nameMap[activity._id] = activity.name;
        }
      });
      setActivityDetails(activityMap);
      setActivityNames(nameMap);
      setPackageDetails(packageResponse);
      setDiscountedTotal(packageResponse.totalPrice || 0);
    } catch (err) {
      setError("Failed to fetch some details. Please try again later.");
    } finally {
      if (!isBackgroundFetch) setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllDetailsForItinerary(itinerary);
  }, [itinerary]);

  // Poll for hotel updates (e.g. Honeymoon Favorite toggle)
  useEffect(() => {
    if (!modifiedItinerary || !modifiedItinerary.itinerary) return;

    const fetchOnlyHotels = async () => {
      try {
        // Use modifiedItinerary to get current hotel IDs including any changed hotels
        const hotelIds = [...new Set(modifiedItinerary.itinerary.flatMap(day => day.hotels?.map(h => h.hotelId) || []))];
        if (hotelIds.length === 0) return;

        const hotelResponses = await Promise.all(hotelIds.map(id => fetch(`${API_BASE_URL}/hotels/${id}`).then(res => res.json())));

        // Merge with existing hotel details instead of replacing entirely
        setHotelDetails(prev => {
          if (!prev) return hotelResponses;
          const merged = [...prev];
          hotelResponses.forEach(newHotel => {
            const existingIndex = merged.findIndex(h => h._id === newHotel._id);
            if (existingIndex >= 0) {
              merged[existingIndex] = newHotel;
            } else {
              merged.push(newHotel);
            }
          });
          return merged;
        });
      } catch (err) {
        console.error("Silent update of hotels failed", err);
      }
    };

    const interval = setInterval(fetchOnlyHotels, 3000);
    return () => clearInterval(interval);
  }, [modifiedItinerary]);

  useEffect(() => {
    const calculateBreakdown = async () => {
      // Calculate breakdown whenever data is available to ensure Total Price is accurate
      if (modifiedItinerary && hotelDetails && cabDetails) {
        try {
          const data = await computeBreakdown();
          setBreakdownData(data);

          // Sync displayed real total price if it differs from stored/estimated price
          if (data?.overall?.finalTotal) {
            const realTotal = data.overall.finalTotal;

            // Update packageDetails display if needed
            if (packageDetails && packageDetails.totalPrice !== realTotal) {
              setPackageDetails(prev => ({ ...prev, totalPrice: realTotal }));
            }

            // Update discountedTotal base if no discount active (or re-calc discount)
            if (!discountApplied && discountedTotal !== realTotal) {
              setDiscountedTotal(realTotal);
            } else if (discountApplied) {
              // If discount applied, we might need to recalculate it based on new total
              // For now, keep logic simple: strict match requires just base sync
              // But if user modified items, discount amount changes. 
              // Let's re-validate promo or just update base.
              // Re-calculating discount might be complex here, so we focus on base price sync.
            }
          }
        } catch (error) {
          console.error("Error calculating breakdown:", error);
        }
      }
    };
    calculateBreakdown();
  }, [modifiedItinerary, hotelDetails, cabDetails, packageDetails?.startDate, packageDetails?.endDate]);

  const computeBreakdown = async () => {
    if (!packageDetails || !modifiedItinerary || !hotelDetails || !cabDetails) {
      return {
        dayBreakdowns: [],
        overall: { hotelsTotal: 0, cabsTotal: 0, activitiesTotal: 0, base: 0, serviceCharge: 0, gst: 0, finalTotal: 0 },
      };
    }

    let overallHotelsTotal = 0, overallCabsTotal = 0, overallActivitiesTotal = 0;

    const dayBreakdowns = await Promise.all(
      (modifiedItinerary.itinerary || []).map(async day => {
        const hotelsTotal = (day.hotels || []).reduce((sum, hotel) => {
          const hotelDetail = hotelDetails.find(h => h._id === hotel.hotelId);
          if (!hotelDetail) return sum; // Skip if hotel details are missing

          let rate = 0; // Start with 0, will be set from DB
          let extraChildRate = 0; // Child charge from DB

          // PRIORITY 1: Try to get seasonal rate from Database
          if (hotelDetail?.seasonalRates && hotelDetail.seasonalRates.length > 0 && packageDetails?.startDate && packageDetails?.endDate) {
            const season = hotelDetail.seasonalRates.find(season => {
              const startSeason = new Date(season.startDate);
              const endSeason = new Date(season.endDate);
              const startPackage = new Date(packageDetails.startDate);
              const endPackage = new Date(packageDetails.endDate);
              return startPackage <= endSeason && endPackage >= startSeason;
            });

            if (season?.rates && season.rates.length > 0) {
              // Try matching by precise Room Name (roomType) first, then by Category (roomCategory)
              const matchingRate = season.rates.find(r =>
                (r.roomType && r.roomType.toLowerCase() === (hotel.roomType || '').toLowerCase()) ||
                (r.roomCategory && r.roomCategory.toLowerCase() === (hotel.roomType || '').toLowerCase())
              );

              const targetRate = matchingRate || season.rates[0];

              // Use correct meal plan rate based on packageDetails.mealPlan
              const mealPlan = packageDetails?.mealPlan || 'CPAI';
              if (mealPlan === 'MAPAI' && targetRate?.specialRateMAPAI) {
                rate = targetRate.specialRateMAPAI;
              } else if (mealPlan === 'APAI' && targetRate?.specialRateAPAI) {
                rate = targetRate.specialRateAPAI;
              } else {
                rate = targetRate?.specialRateCPAI || 0;
              }
              extraChildRate = targetRate?.extraChildRate || 0;
            }
          }

          // PRIORITY 2: If no seasonal rate, use hotel's base price from DB
          if (rate === 0) {
            rate = hotelDetail.price || 0;
          }

          // PRIORITY 3: Last resort fallback to AI-generated price (only if DB has nothing)
          if (rate === 0) {
            rate = hotel.roomPrice || 0;
          }

          // Calculate number of rooms based on guests
          let numberOfRooms = 1;
          let childCharges = 0;

          if (packageDetails?.guests && packageDetails.guests.length > 0) {
            // Calculate total adults across all guest groups (including teenagers 13+)
            let totalAdults = 0;
            let chargeableChildrenCount = 0;

            packageDetails.guests.forEach(g => {
              totalAdults += parseInt(g.adults, 10) || 0;
              const kids = (g.children || []).map(age => parseInt(age, 10)).filter(age => !isNaN(age));

              // Children 13+ count as adults for room calculation
              const teenageAdults = kids.filter(age => age >= 13);
              totalAdults += teenageAdults.length;

              // Children 5-12 are charged extraChildRate
              const chargeableKids = kids.filter(age => age >= 5 && age <= 12);
              chargeableChildrenCount += chargeableKids.length;
            });

            numberOfRooms = Math.ceil((totalAdults || 1) / 2); // 2 adults per room

            // Calculate child charges
            if (extraChildRate > 0) {
              childCharges = chargeableChildrenCount * extraChildRate;
            }
          }

          const nights = hotel.nights || 1;
          return sum + ((rate * numberOfRooms + childCharges) * nights);
        }, 0);

        const cabsTotal = (day.cabs || []).reduce((sum, cab) => {
          const cabDetail = cabDetails.find(c => c._id === cab.cabId);
          if (!cabDetail) return sum; // Skip if cab details are missing
          let pricePerDay = cab.price || cabDetail.pricePerDay || 0;
          if (cabDetail?.seasonalRates?.length > 0 && packageDetails?.startDate && packageDetails?.endDate) {
            const startPackage = new Date(packageDetails.startDate);
            const endPackage = new Date(packageDetails.endDate);
            const matchedSeasonalRate = cabDetail.seasonalRates.find(rate => {
              const startSeason = new Date(rate.startDate);
              const endSeason = new Date(rate.endDate);
              return startPackage <= endSeason && endPackage >= startSeason;
            });
            if (matchedSeasonalRate) {
              pricePerDay = matchedSeasonalRate.pricePerDay;
            }
          }
          return sum + (cab.days * pricePerDay);
        }, 0);

        const activitiesTotal = (day.destinations || []).reduce((sum, dest) => {
          const destTotal = (dest.activities || []).reduce((actSum, activity) => {
             const detail = activityDetails && activityDetails[activity.activityId];
             return actSum + (detail && detail.rates ? detail.rates : 0);
          }, 0);
          return sum + destTotal;
        }, 0);

        const dayBase = hotelsTotal + cabsTotal + activitiesTotal;
        const dayService = dayBase * 0.1;  // 10% service charge on base
        const dayGST = dayService * 0.18;  // 18% GST on service charge ONLY (NEW formula)
        const dayTotal = dayBase + dayService + dayGST;

        overallHotelsTotal += hotelsTotal;
        overallCabsTotal += cabsTotal;
        overallActivitiesTotal += activitiesTotal;

        return { day: day.day, hotelsTotal, cabsTotal, activitiesTotal, dayBase, dayService, dayGST, dayTotal };
      })
    );

    const baseEstimate = overallHotelsTotal + overallCabsTotal + overallActivitiesTotal;
    const serviceCharge = baseEstimate * 0.1;  // 10% service charge on base
    const gst = serviceCharge * 0.18;  // 18% GST on service charge ONLY (NEW formula)
    const finalTotal = baseEstimate + serviceCharge + gst;

    return {
      dayBreakdowns,
      overall: { hotelsTotal: overallHotelsTotal, cabsTotal: overallCabsTotal, activitiesTotal: overallActivitiesTotal, base: baseEstimate, serviceCharge, gst, finalTotal },
    };
  };

  const updateTotalPrice = async (updatedItinerary) => {
    try {
      const breakdown = await computeBreakdown();
      const newTotalPrice = breakdown.overall.finalTotal;

      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");

      // Validate itinerary, allowing no hotels on the last day
      const maxDay = Math.max(...updatedItinerary.itinerary.map(d => d.day));
      updatedItinerary.itinerary.forEach(day => {
        if (day.day < maxDay && (!day.hotels || day.hotels.length === 0)) {
          throw new Error(`Hotels are required for day ${day.day}, except for the last day.`);
        }
        day.hotels?.forEach(hotel => {
          if (!hotel.hotelId || !hotel.roomType || !hotel.nights) {
            throw new Error(`Invalid hotel data for day ${day.day}: missing hotelId, roomType, or nights.`);
          }
        });
      });

      const packageData = {
        ...updatedItinerary,
        totalPrice: newTotalPrice,
      };

      const requestOptions = {
        method: "PUT",
        headers: myHeaders,
        body: JSON.stringify(packageData),
        redirect: "follow",
      };

      const response = await fetch(`${API_BASE_URL}/packages/${updatedItinerary._id}`, requestOptions);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to update price: ${errorData.message || response.status}`);
      }
      const result = await response.json();
      setPackageDetails(prev => ({ ...prev, totalPrice: newTotalPrice }));
      setDiscountedTotal(newTotalPrice);
      return result;
    } catch (err) {
      console.error("Failed to update package price:", err);
      setError(err.message || "Failed to update package price. Please try again.");
      throw err;
    }
  };

  const applyPromoCode = async (codeToApply = null) => {
    // Only use codeToApply if it's a valid string (not an event object)
    const code = (typeof codeToApply === 'string' && codeToApply) ? codeToApply : promoCode;

    // Validate that code is a string and not empty
    if (!code || typeof code !== 'string' || !code.trim()) {
      setPromoError('Please enter a promo code');
      return;
    }

    if (!breakdownData) {
      setPromoError('Please wait for pricing to load');
      return;
    }

    try {
      // Get base price and final total for validation
      const basePrice = breakdownData.overall.base;
      const finalTotal = breakdownData.overall.finalTotal;  // Total Package Cost for min amount check

      const response = await fetch(`${API_BASE_URL}/promocodes/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: code,
          totalAmount: finalTotal,  // Use final total for minimum purchase check
        }),
      });
      const result = await response.json();
      if (response.ok) {
        if (!result.discount) {
          setPromoError('Invalid backend response');
          return;
        }

        // New Calculation: Cost Price + [(Service Charge - Discount) + GST]
        // Where: Service Charge = 10% of Cost Price
        //        Discount = applied to Service Charge (discount percentage on service charge)
        //        GST = 18% of (Service Charge - Discount)

        const serviceCharge = basePrice * 0.1;  // 10% service charge
        const discountAmt = serviceCharge * (result.discount / 100);  // Discount on service charge
        const netServiceCharge = serviceCharge - discountAmt;  // Service charge after discount
        const gst = netServiceCharge * 0.18;  // 18% GST on net service charge
        const newFinalTotal = basePrice + netServiceCharge + gst;  // Net Payable Amount

        setDiscountAmount(discountAmt);
        setDiscountedTotal(newFinalTotal);
        setDiscountPercentage(result.discount);
        setDiscountApplied(true);
        setPromoError('');
      } else {
        setDiscountApplied(false);
        setDiscountedTotal(packageDetails ? packageDetails.totalPrice : 0);
        setDiscountAmount(0);
        setDiscountPercentage(0);
        setPromoError(result.message || 'Invalid promocode');
      }
    } catch (error) {
      console.error('Error validating promocode:', error);
      setDiscountApplied(false);
      setDiscountedTotal(packageDetails ? packageDetails.totalPrice : 0);
      setDiscountAmount(0);
      setDiscountPercentage(0);
      setPromoError('Error validating promocode');
    }
  };

  const removePromoCode = () => {
    setDiscountApplied(false);
    setDiscountAmount(0);
    setDiscountPercentage(0);
    setPromoCode('');
    setPromoError('');
    // Reset to original total
    if (packageDetails) {
      setDiscountedTotal(packageDetails.totalPrice);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (value.trim()) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if ((name === 'email' || name === 'phone') && !value.trim()) {
      setErrors(prev => ({
        ...prev,
        [name]: `${name.charAt(0).toUpperCase() + name.slice(1)} is required`,
      }));
    }
  };

  const validateForm = () => {
    let newErrors = {};
    if (!formData.email.trim()) newErrors.email = "Email is required";
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoadingSubmit(true);
    const breakdown = await computeBreakdown();
    const originalTotal = breakdown.overall.finalTotal;

    const payload = {
      ...formData,
      totalPrice: originalTotal,
      finalPrice: discountedTotal,
      discountApplied: discountApplied,
      discountPercentage: discountPercentage,
    };

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    // Idempotency key to prevent duplicate bookings on retry
    myHeaders.append("X-Idempotency-Key", `booking-${formData.email}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: JSON.stringify(payload),
      redirect: "follow",
    };

    fetch(EMAIL_API_URL, requestOptions)
      .then(response => response.text())
      .then(result => {
        console.log(result);
        alert("Details submitted successfully!");
        setFormModalOpen(false);
        // Show feedback form after successful booking
        showBookingFeedback(formData.email);
        // Redirect to home page after showing feedback
        setTimeout(() => {
          navigate('/');
        }, 500);
      })
      .catch(error => {
        console.error(error);
        alert("Failed to submit details.");
      })
      .finally(() => {
        setLoadingSubmit(false);
      });
  };

  const handleModalClose = (e) => {
    if (e.target === e.currentTarget) {
      setFormModalOpen(false);
      setChangeModalOpen(false);
      setIsBreakdownModalOpen(false);
    }
  };

  const handleChangeClick = async (type, data, day, index) => {
    setChangeModalType(type);
    setChangeModalData(data);
    setChangeModalDay(day);
    setChangeModalIndex(index);
    setAlternativeOptions([]);
    setLoadingAlternatives(true);
    setChangeModalOpen(true);

    try {
      let alternatives = [];
      if (type === 'hotel') {
        const destination = modifiedItinerary.itinerary[day - 1].destinations[0];
        const destDetails = destinationDetails.find(d => d._id === destination.destinationId);
        if (destDetails) {
          const response = await fetch(`${API_BASE_URL}/hotels?destination=${destDetails.name}`);
          const data = await response.json();
          alternatives = data.filter(hotel => hotel._id !== modifiedItinerary.itinerary[day - 1].hotels[index]?.hotelId);
        }
      } else if (type === 'cab') {
        const destination = modifiedItinerary.itinerary[day - 1].destinations[0];
        const destDetails = destinationDetails.find(d => d._id === destination.destinationId);
        if (destDetails) {
          const response = await fetch(`${API_BASE_URL}/cabs/destination?destination=${destDetails.name}`);
          const data = await response.json();
          alternatives = data.filter(cab => cab._id !== modifiedItinerary.itinerary[day - 1].cabs[index]?.cabId);
        }
      } else if (type === 'activity') {
        const destination = modifiedItinerary.itinerary[day - 1].destinations[0];
        if (destination) {
          const response = await fetch(`${API_BASE_URL}/activities/destination/${destination.destinationId}`);
          const activityData = await response.json();
          const currentActivities = modifiedItinerary.itinerary[day - 1].destinations
            .find(d => d.destinationId === destination.destinationId)
            ?.activities?.map(a => a.activityId) || [];
          alternatives = activityData.filter(activity => !currentActivities.includes(activity._id));
        }
      }
      setAlternativeOptions(alternatives);
    } catch (err) {
      console.error("Failed to fetch alternatives:", err);
      setError("Failed to fetch alternative options. Please try again later.");
    } finally {
      setLoadingAlternatives(false);
    }
  };

  const handleChangeSelection = async (newOptionId) => {
    const updatedItinerary = JSON.parse(JSON.stringify(modifiedItinerary));
    const dayIndex = changeModalDay - 1;
    const maxDay = Math.max(...updatedItinerary.itinerary.map(d => d.day));

    try {
      if (changeModalType === 'hotel') {
        // Prevent adding hotels to the last day
        if (dayIndex === maxDay - 1) {
          throw new Error("Hotels cannot be added to the last day of the package.");
        }
        if (!updatedItinerary.itinerary[dayIndex].hotels) {
          updatedItinerary.itinerary[dayIndex].hotels = [];
        }
        const response = await fetch(`${API_BASE_URL}/hotels/${newOptionId}`);
        if (!response.ok) throw new Error(`Failed to fetch hotel data: ${response.status}`);
        const hotelData = await response.json();

        let rate = hotelData.price || 0;
        // Get the current room type from itinerary (if changing existing hotel)
        let currentRoomType = "Standard room";
        if (changeModalIndex !== null && updatedItinerary.itinerary[dayIndex].hotels[changeModalIndex]) {
          currentRoomType = updatedItinerary.itinerary[dayIndex].hotels[changeModalIndex].roomType || "Standard room";
        }

        if (hotelData.seasonalRates && hotelData.seasonalRates.length > 0 && packageDetails?.startDate && packageDetails?.endDate) {
          const season = hotelData.seasonalRates.find(season => {
            const startSeason = new Date(season.startDate);
            const endSeason = new Date(season.endDate);
            const startPackage = new Date(packageDetails.startDate);
            const endPackage = new Date(packageDetails.endDate);
            return startPackage <= endSeason && endPackage >= startSeason;
          });
          if (season?.rates && season.rates.length > 0) {
            // Match by roomType name or roomCategory
            const matchingRate = season.rates.find(r =>
              (r.roomType && r.roomType.toLowerCase() === currentRoomType.toLowerCase()) ||
              (r.roomCategory && r.roomCategory.toLowerCase() === currentRoomType.toLowerCase())
            );
            rate = matchingRate?.specialRateCPAI || season.rates[0]?.specialRateCPAI || rate;
          }
        }

        if (changeModalIndex === null || changeModalIndex >= updatedItinerary.itinerary[dayIndex].hotels.length) {
          updatedItinerary.itinerary[dayIndex].hotels.push({
            hotelId: newOptionId,
            name: hotelData.name || "Unknown Hotel",
            roomType: currentRoomType,
            nights: 1,
            roomPrice: rate,
          });
        } else {
          const currentHotel = updatedItinerary.itinerary[dayIndex].hotels[changeModalIndex];
          updatedItinerary.itinerary[dayIndex].hotels[changeModalIndex] = {
            hotelId: newOptionId,
            name: hotelData.name || currentHotel.name || "Unknown Hotel",
            roomType: currentHotel.roomType || currentRoomType,
            nights: currentHotel.nights || 1,
            roomPrice: rate,
          };
        }

        setHotelDetails(prev => {
          const newDetails = prev ? [...prev] : [];
          const existingIndex = newDetails.findIndex(h => h._id === newOptionId);
          if (existingIndex >= 0) {
            newDetails[existingIndex] = hotelData;
          } else {
            newDetails.push(hotelData);
          }
          return newDetails;
        });
      } else if (changeModalType === 'cab') {
        const response = await fetch(`${API_BASE_URL}/cabs/${newOptionId}`);
        if (!response.ok) throw new Error(`Failed to fetch cab data: ${response.status}`);
        const cabData = await response.json();

        let pricePerDay = cabData.pricePerDay || 0;
        if (cabData.seasonalRates?.length > 0 && packageDetails?.startDate && packageDetails?.endDate) {
          const startPackage = new Date(packageDetails.startDate);
          const endPackage = new Date(packageDetails.endDate);
          const matchedSeasonalRate = cabData.seasonalRates.find(rate => {
            const startSeason = new Date(rate.startDate);
            const endSeason = new Date(rate.endDate);
            return startPackage <= endSeason && endPackage >= startSeason;
          });
          if (matchedSeasonalRate) {
            pricePerDay = matchedSeasonalRate.pricePerDay;
          }
        }

        // Ensure cabs array exists
        if (!updatedItinerary.itinerary[dayIndex].cabs) {
          updatedItinerary.itinerary[dayIndex].cabs = [];
        }

        const newCabEntry = {
          cabId: newOptionId,
          name: cabData.name || "Unknown Cab",
          days: 1,
          price: pricePerDay,
        };

        // If cabs array is empty or index doesn't exist, push new cab
        if (updatedItinerary.itinerary[dayIndex].cabs.length === 0 || changeModalIndex >= updatedItinerary.itinerary[dayIndex].cabs.length) {
          updatedItinerary.itinerary[dayIndex].cabs.push(newCabEntry);
        } else {
          // Update existing cab at index
          updatedItinerary.itinerary[dayIndex].cabs[changeModalIndex] = {
            ...updatedItinerary.itinerary[dayIndex].cabs[changeModalIndex],
            ...newCabEntry,
          };
        }

        setCabDetails(prev => {
          const newDetails = prev ? [...prev] : [];
          const existingIndex = newDetails.findIndex(c => c._id === newOptionId);
          if (existingIndex >= 0) {
            newDetails[existingIndex] = cabData;
          } else {
            newDetails.push(cabData);
          }
          return newDetails;
        });
      } else if (changeModalType === 'activity') {
        // Validate activityId before proceeding
        if (!newOptionId) {
          throw new Error("Invalid activity ID");
        }

        const destIndex = updatedItinerary.itinerary[dayIndex].destinations.findIndex(
          d => d.destinationId === changeModalData.destinationId
        );

        if (destIndex !== -1) {
          // Ensure activities array exists
          if (!updatedItinerary.itinerary[dayIndex].destinations[destIndex].activities) {
            updatedItinerary.itinerary[dayIndex].destinations[destIndex].activities = [];
          }

          // Add activity with proper structure
          updatedItinerary.itinerary[dayIndex].destinations[destIndex].activities.push({
            activityId: newOptionId
          });

          // Fetch activity name for display
          try {
            const response = await fetch(`${API_BASE_URL}/activities/${newOptionId}`);
            if (response.ok) {
              const data = await response.json();
              setActivityNames(prev => ({ ...prev, [newOptionId]: data.name }));
              setActivityDetails(prev => ({ ...prev, [newOptionId]: data }));
            }
          } catch (err) {
            console.warn("Failed to fetch activity name:", err);
          }
        } else {
          throw new Error("Destination not found in itinerary");
        }
      }

      setModifiedItinerary(updatedItinerary);
      await updateTotalPrice(updatedItinerary);

      alert("Package updated successfully!");
    } catch (err) {
      console.error("Failed to update package:", err);
      setError(err.message || "Failed to update package. Please try again.");
      alert(err.message || "Failed to update package. Please try again.");
    } finally {
      setChangeModalOpen(false);
    }
  };

  const handleRemoveActivity = async (dayIndex, destIndex, activityIndex) => {
    try {
      const updatedItinerary = JSON.parse(JSON.stringify(modifiedItinerary));
      updatedItinerary.itinerary[dayIndex].destinations[destIndex].activities.splice(activityIndex, 1);
      setModifiedItinerary(updatedItinerary);
      await updateTotalPrice(updatedItinerary);
      alert("Activity removed successfully!");
    } catch (err) {
      console.error("Failed to remove activity:", err);
      setError(err.message || "Failed to remove activity. Please try again.");
      alert(err.message || "Failed to remove activity. Please try again.");
    }
  };



  if (!itinerary) {
    return (
      <div className="trip-detail-container" style={{ textAlign: "center", paddingTop: "100px" }}>
        <h3>No itinerary details found.</h3>
        <button className="btn-nav" onClick={() => navigate("/")}>Go Home</button>
      </div>
    );
  }

  const handleUnlockSubmit = async (e) => {
    e.preventDefault();
    if (!unlockEmail) return;
    setUnlockLoading(true);
    try {
      // 1. Capture lead
      await fetch(`${API_BASE_URL}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: "Plan Unlock User",
          email: unlockEmail,
          mobile: "Not Provided",
          actionType: "save",
          destination: packageDetails?.state || itinerary?.stateName,
          status: "New",
          itineraryTitle: itinerary?.title || itinerary?.name || "AI Generated Trip",
        }),
      });
      // 2. Send itinerary email (deliver the promise)
      if (packageDetails?._id) {
        fetch(EMAIL_API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            selectedPackage: packageDetails._id,
            email: unlockEmail,
            name: "Traveler",
            phone: "Not Provided",
            totalPrice: breakdownData?.overall?.finalTotal || packageDetails.totalPrice,
            finalPrice: discountedTotal || breakdownData?.overall?.finalTotal || packageDetails.totalPrice,
            discountApplied: discountApplied,
            discountPercentage: discountPercentage,
          }),
        }).catch(err => console.warn("Email delivery failed:", err));
      }
    } catch (err) {
      console.warn("Failed to capture unlock lead:", err);
    }
    setUnlockLoading(false);
    setIsPlanUnlocked(true);
    setShowUnlockModal(false);
    // 3. Show success toast
    setShowUnlockToast(true);
    setTimeout(() => setShowUnlockToast(false), 3200);
    // 4. Smooth scroll to timeline
    setTimeout(() => {
      const timeline = document.querySelector('.timeline-container');
      if (timeline) timeline.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
  };

  // Share With Partner handler
  const handleShareSubmit = async (e) => {
    e.preventDefault();
    if (!shareForm.email) return;
    setShareSubmitting(true);
    try {
      // Capture partner as lead
      await fetch(`${API_BASE_URL}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: shareForm.name || "Travel Partner",
          email: shareForm.email,
          mobile: "Not Provided",
          actionType: "save",
          destination: packageDetails?.state || itinerary?.stateName,
          status: "New",
          itineraryTitle: `Shared: ${itinerary?.title || itinerary?.name || "AI Trip"}`
        }),
      });
      // Send itinerary email to partner
      if (packageDetails?._id) {
        await fetch(EMAIL_API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            selectedPackage: packageDetails._id,
            email: shareForm.email,
            name: shareForm.name || "Travel Partner",
            phone: "Not Provided",
            totalPrice: breakdownData?.overall?.finalTotal || packageDetails.totalPrice,
            finalPrice: discountedTotal || breakdownData?.overall?.finalTotal || packageDetails.totalPrice,
            discountApplied: discountApplied,
            discountPercentage: discountPercentage,
          }),
        });
      }
      setShareSuccess(true);
    } catch (err) {
      console.warn("Share failed:", err);
    }
    setShareSubmitting(false);
  };

  if (isRefreshing) {
    return (
      <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 9999, display: "flex", justifyContent: "center", alignItems: "center", backdropFilter: "blur(4px)" }}>
        <div style={{ background: "white", padding: "40px", borderRadius: "20px", boxShadow: "0 10px 40px rgba(0,0,0,0.2)", width: "90%", maxWidth: "600px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div className="spinner" style={{ width: "50px", height: "50px", borderWidth: "4px" }}></div>
          <h2 style={{ fontFamily: '"Merriweather", serif', color: "#004e61", fontSize: "28px", marginTop: "24px", marginBottom: "12px" }}>Generating Your Trip...</h2>
          <p style={{ color: "#666", fontSize: "16px", marginBottom: "30px" }}>{loadingMessage}</p>

          {/* Fun Facts Section */}
          <div style={{
            marginTop: "10px",
            padding: "24px",
            background: "#f0f9fa",
            borderRadius: "16px",
            maxWidth: "500px",
            width: "100%",
            animation: "fadeIn 0.5s ease-in-out"
          }}>
            <h4 style={{
              fontFamily: '"Merriweather", serif',
              color: "#004e61",
              fontSize: "18px",
              marginBottom: "12px"
            }}>Did You Know?</h4>
            <p style={{
              fontFamily: '"Poppins", sans-serif',
              color: "#555",
              fontSize: "15px",
              lineHeight: "1.6",
              minHeight: "48px"
            }}>
              {funFacts[currentFactIndex]}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="trip-detail-container" style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <div className="spinner"></div>
        <p className="loading-text" style={{ marginLeft: "15px" }}>Loading details...</p>
      </div>
    );
  }

  const renderPlanScore = () => {
    const score = packageDetails?.planScore;
    if (!score || score.total == null) return null;

    const total = Math.round(score.total);
    const maxScore = score.maxScore || 100;
    const grade = score.grade || 'Good Match';
    const gradeEmoji = score.gradeEmoji || '👍';
    const positives = score.positives || [];
    const warnings = score.warnings || [];
    const factors = score.factors || {};

    // SVG circle math
    const radius = 65;
    const circumference = 2 * Math.PI * radius;
    const progress = total / maxScore;
    const dashOffset = circumference * (1 - progress);

    // Color logic
    let colorClass = 'score-green';
    let gradeClass = 'grade-green';
    let fillClass = 'fill-green';
    if (total < 60) {
      colorClass = 'score-red';
      gradeClass = 'grade-red';
      fillClass = 'fill-red';
    } else if (total < 80) {
      colorClass = 'score-yellow';
      gradeClass = 'grade-yellow';
      fillClass = 'fill-yellow';
    }

    // Grade subtitle text
    let gradeSubtitle = 'This plan matches your travel style and budget well.';
    if (total >= 90) gradeSubtitle = 'This plan is an excellent match for your preferences.';
    else if (total >= 80) gradeSubtitle = 'This plan matches your travel style and budget well.';
    else if (total >= 60) gradeSubtitle = 'This plan is a good fit with some areas to consider.';
    else if (total >= 40) gradeSubtitle = 'This plan could be improved to better match your preferences.';
    else gradeSubtitle = 'Consider customizing this plan to better fit your needs.';

    // Factor display data
    const factorList = [
      { name: 'Preference Match', score: factors.preferenceMatch?.score || 0, max: factors.preferenceMatch?.maxScore || 40 },
      { name: 'Budget Match', score: factors.budgetMatch?.score || 0, max: factors.budgetMatch?.maxScore || 20 },
      { name: 'Trip Flow', score: factors.tripFlow?.score || 0, max: factors.tripFlow?.maxScore || 20 },
      { name: 'Experience Coverage', score: factors.experienceCoverage?.score || 0, max: factors.experienceCoverage?.maxScore || 20 },
    ];

    const getBarFillClass = (score, max) => {
      const pct = max > 0 ? (score / max) * 100 : 0;
      if (pct >= 75) return 'fill-green';
      if (pct >= 50) return 'fill-yellow';
      return 'fill-red';
    };

    return (
      <section className="plan-score-section">
        <div className="plan-score-header">
          <h3>Your Plan Score</h3>
          <p>How well this plan matches your preferences</p>
        </div>

        {/* Ring + Grade */}
        <div className="plan-score-ring-row">
          <div className="plan-score-ring-wrapper">
            <svg className="plan-score-ring-svg" viewBox="0 0 160 160">
              <circle className="plan-score-ring-bg" cx="80" cy="80" r={radius} />
              <circle
                className={`plan-score-ring-progress ${colorClass}`}
                cx="80" cy="80" r={radius}
                strokeDasharray={circumference}
                strokeDashoffset={dashOffset}
              />
            </svg>
            <div className="plan-score-ring-text">
              <div className="plan-score-number">{total}</div>
              <div className="plan-score-max">/{maxScore}</div>
            </div>
          </div>

          <div className="plan-score-grade-info">
            <div className={`plan-score-grade-badge ${gradeClass}`}>
              <span>{gradeEmoji}</span> {grade}
            </div>
            <div className="plan-score-grade-subtitle">{gradeSubtitle}</div>
          </div>
        </div>

        {/* Explanation Layer */}
        {(positives.length > 0 || warnings.length > 0) && (
          <div className="plan-score-explanation">
            {positives.length > 0 && (
              <div className="plan-score-positives">
                <h4><span className="plan-score-check-icon">✔</span> Why this score</h4>
                <ul>
                  {positives.map((item, idx) => (
                    <li key={idx}>
                      <span className="plan-score-check-icon" style={{ color: '#10b981' }}>✔</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {warnings.length > 0 && (
              <div className="plan-score-warnings">
                <h4><span className="plan-score-check-icon">⚠</span> Needs improvement</h4>
                <ul>
                  {warnings.map((item, idx) => (
                    <li key={idx}>
                      <span className="plan-score-check-icon" style={{ color: '#f59e0b' }}>⚠</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Factor Breakdown */}
        <div className="plan-score-factors">
          <h4>Score Breakdown</h4>
          {factorList.map((factor, idx) => {
            const pct = factor.max > 0 ? Math.round((factor.score / factor.max) * 100) : 0;
            return (
              <div className="plan-score-factor-item" key={idx}>
                <div className="plan-score-factor-label">
                  <span className="plan-score-factor-name">{factor.name}</span>
                  <span className="plan-score-factor-value">{factor.score}/{factor.max}</span>
                </div>
                <div className="plan-score-factor-track">
                  <div
                    className={`plan-score-factor-fill ${getBarFillClass(factor.score, factor.max)}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  const renderRegretMinimization = () => {
    const prefs = packageDetails?.couplePreferences || itinerary?.couplePreferences || {};

    let worksBecause = [];
    let avoided = [];

    const destCount = packageDetails?.itinerary?.reduce((acc, day) => {
      day.destinations?.forEach(d => acc.add(d.destinationId?._id || d.destinationId));
      return acc;
    }, new Set()).size || 1;

    // DYNAMIC COPY RULE
    // 1. PACE
    if (prefs.pace === 'Chill (slow & relaxed)' || prefs.pace === 'chill') {
      worksBecause.push("Based on your preference for a relaxed trip, your days are kept easy with enough free time");
      avoided.push("No hectic or rushed days");
    } else if (prefs.pace === 'Packed (see it all)' || prefs.pace === 'packed') {
      worksBecause.push("Your plan is designed to cover maximum places within your available time");
      avoided.push("No wasted idle time");
    } else {
      worksBecause.push("Your itinerary is balanced — covering key experiences without feeling rushed");
      avoided.push("No overpacked itinerary");
    }

    // 2. TRIP TYPE
    if (prefs.tripType === 'Budget' || prefs.tripType === 'budget') {
      worksBecause.push("Stays and experiences are optimized to give you the best value within your budget");
      avoided.push("No overpriced hotel options");
    } else if (prefs.tripType === 'Luxury' || prefs.tripType === 'luxury') {
      worksBecause.push("Your Luxury trip includes premium stays and high-quality experiences throughout");
      avoided.push("No compromise on stay quality");
    } else {
      worksBecause.push("Your Comfort trip includes well-rated 4★ stays with a strong balance of quality and price");
    }

    // 3. DESTINATION LOGIC
    if (destCount <= 2) {
      worksBecause.push("Fewer destinations mean more time to actually enjoy each place without rushing");
    } else if (destCount === 3) {
      worksBecause.push("Your route is optimized to cover multiple destinations without long tiring travel");
    } else {
      worksBecause.push("Your route is structured efficiently to cover more places without unnecessary backtracking");
    }

    // 4. DESTINATION COUNT AVOIDED
    if (destCount <= 3) {
      avoided.push("No long tiring travel between cities");
    }

    // 5. INTENT
    if (prefs.intent === 'Relax & unwind' || prefs.intent === 'relax') {
      worksBecause.push("The plan focuses on peaceful moments and quality time together");
    } else if (prefs.intent === 'Explore places' || prefs.intent === 'explore') {
      worksBecause.push("Key sightseeing and must-do experiences are included in your plan");
    } else {
      worksBecause.push("You get a great mix of relaxation and exploration across your trip");
    }

    // 6. BUDGET MATCH
    if (prefs.budgetMatch === 'match') {
      worksBecause.push("This plan fits well within your expected budget");
    } else if (prefs.budgetMatch === 'slight_over') {
      worksBecause.push("Slightly above budget, but offers a better overall experience");
    } else if (prefs.budgetMatch === 'over') {
      worksBecause.push("This plan can be optimized further to better match your budget");
    }

    // Enforce limits
    worksBecause = worksBecause.slice(0, 4);
    avoided = avoided.slice(0, 3);

    if (worksBecause.length === 0 && avoided.length === 0) return null;

    return (
      <section className="regret-minimization-section" style={{
        marginTop: "40px",
        marginBottom: "40px",
        padding: "30px",
        background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
        borderRadius: "20px",
        border: "1px solid #bbf7d0",
        boxShadow: "0 10px 30px rgba(0,0,0,0.05)"
      }}>
        <h3 style={{ fontSize: "24px", color: "#166534", marginBottom: "24px", display: "flex", alignItems: "center", gap: "12px", fontFamily: '"Merriweather", serif' }}>
          <span>✨</span> Why this plan works for you
        </h3>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "24px" }}>
          <div style={{ background: "white", padding: "24px", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px rgba(0,0,0,0.02)" }}>
            <h4 style={{ color: "#059669", fontSize: "18px", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px", fontFamily: '"Poppins", sans-serif' }}>
              <span style={{ backgroundColor: "#d1fae5", color: "#059669", padding: "4px 8px", borderRadius: "8px", fontSize: "14px" }}>✓</span> What we included
            </h4>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: "15px", color: "#334155", display: "flex", flexDirection: "column", gap: "12px", fontFamily: '"Poppins", sans-serif' }}>
              {worksBecause.map((item, idx) => (
                <li key={idx} style={{ display: "flex", gap: "12px", lineHeight: "1.5" }}>
                  <span style={{ color: "#10b981", fontSize: "18px" }}>•</span> {item}
                </li>
              ))}
            </ul>
          </div>

          <div style={{ background: "white", padding: "24px", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px rgba(0,0,0,0.02)" }}>
            <h4 style={{ color: "#ef4444", fontSize: "18px", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px", fontFamily: '"Poppins", sans-serif' }}>
              <span style={{ backgroundColor: "#fee2e2", color: "#ef4444", padding: "4px 8px", borderRadius: "8px", fontSize: "14px" }}>✕</span> What we avoided
            </h4>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: "15px", color: "#334155", display: "flex", flexDirection: "column", gap: "12px", fontFamily: '"Poppins", sans-serif' }}>
              {avoided.map((item, idx) => (
                <li key={idx} style={{ display: "flex", gap: "12px", lineHeight: "1.5" }}>
                  <span style={{ color: "#f43f5e", fontSize: "18px" }}>•</span> {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

      </section>
    );
  };

  const renderCTABlock = () => {
    return (
      <div className="regret-cta-block" style={{ marginTop: "32px", display: "flex", flexDirection: "column", gap: "16px", alignItems: "center", marginBottom: "40px" }}>

        {/* Micro Nudge */}
        <div style={{ color: "#64748b", fontSize: "14px", fontWeight: "500", marginBottom: "-8px" }}>
          You’re one step away from finalizing your trip
        </div>

        {/* Trust Strip */}
        <div className="trust-strip" style={{ display: 'flex', gap: '16px', fontSize: '13px', color: '#64748b', flexWrap: 'wrap', justifyContent: 'center' }}>
          <span className="trust-item"><span className="trust-icon" style={{ color: "#10b981" }}>✔</span> Based on trips planned for 2,400+ couples</span>
          <span className="trust-item"><span className="trust-icon" style={{ color: "#10b981" }}>✔</span> 100% free • No spam</span>
        </div>

        <button
          className="btn-primary"
          onClick={() => setIsCustomizeVisible(true)}
          style={{ padding: "16px 40px", fontSize: "17px", fontWeight: "700", width: "100%", maxWidth: "420px", borderRadius: "14px", boxShadow: "0 6px 20px rgba(28, 168, 203, 0.35)", background: "linear-gradient(135deg, #1CA8CB 0%, #004E61 100%)", color: "white", border: "none", cursor: "pointer", letterSpacing: "0.3px" }}
        >
          Customize This Plan Your Way
        </button>

        {!isPlanUnlocked && (
          <button
            className="btn-secondary"
            onClick={() => setShowUnlockModal(true)}
            style={{ padding: "14px 32px", fontSize: "16px", fontWeight: "600", width: "100%", maxWidth: "420px", borderRadius: "12px", background: "white", color: "#334155", border: "1px solid #cbd5e1", cursor: "pointer" }}
          >
            View Exact Hotels & Pricing
          </button>
        )}

        <button
          className="expert-link"
          onClick={() => setIsContactCaptureOpen(true)}
          style={{ background: "none", border: "none", color: "#10b981", fontSize: "14px", fontWeight: "500", textDecoration: "underline", cursor: "pointer", marginTop: "8px" }}
        >
          Need help deciding? Get expert advice →
        </button>
      </div>
    );
  };

  return (
    <div className="trip-detail-container">
      {/* Navigation Header */}
      <header className="trip-header">
        <div className="nav-brand">TripPro AI</div>
        <button className="btn-nav" onClick={() => navigate("/")}>
          &larr; Back to Home
        </button>
      </header>

      {/* Hero Section */}
      {packageDetails && (
        <section className="trip-hero">
          {packageDetails.coverImage && (
            <img src={packageDetails.coverImage} alt="Trip Cover" className="hero-cover" />
          )}
          <div className="hero-content">
            <h1 className="hero-title">{packageDetails.name || "Your Dream Itinerary"}</h1>

            <div className="hero-meta">
              <div className="meta-item">
                <span>🗓</span>
                <span>{packageDetails.startDate ? new Date(packageDetails.startDate).toLocaleDateString() : "TBD"} - {packageDetails.endDate ? new Date(packageDetails.endDate).toLocaleDateString() : "TBD"}</span>
              </div>
              <div className="meta-item">
                <span>⏱</span>
                <span>{modifiedItinerary?.itinerary?.length || 0} Days</span>
              </div>
            </div>

            <p style={{ color: "#555", lineHeight: "1.6", marginBottom: "20px" }}>
              {packageDetails.description}
            </p>
          </div>
        </section>
      )}

      {renderPlanScore()}

      {renderRegretMinimization()}

      {renderCTABlock()}

      {/* Timeline Section */}
      <div className="timeline-container">
        <div className="timeline-line"></div>
        {modifiedItinerary && modifiedItinerary.itinerary && modifiedItinerary.itinerary.map((day, dayIndex) => {
          // Calculate dynamic date if startDate exists
          const dateDisplay = packageDetails?.startDate
            ? new Date(new Date(packageDetails.startDate).setDate(new Date(packageDetails.startDate).getDate() + (day.day - 1))).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
            : `Day ${day.day}`;

          // Check if destination changed from previous day — show travel card
          let travelCard = null;
          if (dayIndex > 0) {
            const prevDay = modifiedItinerary.itinerary[dayIndex - 1];
            const prevDestName = prevDay.destinations?.[0]?.name || '';
            const currentDestName = day.destinations?.[0]?.name || '';

            if (prevDestName && currentDestName && prevDestName.toLowerCase() !== currentDestName.toLowerCase()) {
              // Find matching travel segment from planScore
              const segments = packageDetails?.planScore?.travelSegments || [];
              const matchedSegment = segments.find(s =>
                s.from?.toLowerCase() === prevDestName.toLowerCase() &&
                s.to?.toLowerCase() === currentDestName.toLowerCase()
              );

              const hours = matchedSegment?.durationHours || null;
              const km = matchedSegment?.distanceKm || null;

              // Format duration as "Xh Ym"
              let durationText = '';
              if (hours) {
                const h = Math.floor(hours);
                const m = Math.round((hours - h) * 60);
                durationText = h > 0 ? `${h}h ${m}m` : `${m}m`;
              }

              travelCard = (
                <div className="travel-segment-card" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  padding: '14px 20px',
                  margin: '0 0 8px 60px',
                  background: 'linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%)',
                  borderRadius: '12px',
                  border: '1px solid #bfdbfe',
                  fontSize: '14px',
                  color: '#1e40af',
                  fontWeight: '500',
                }}>
                  <span style={{ fontSize: '20px' }}>🚗</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', color: '#1e3a5f', marginBottom: '2px' }}>
                      Drive to {currentDestName}
                    </div>
                    <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: '#3b82f6', flexWrap: 'wrap' }}>
                      {km && <span>📍 {km} km</span>}
                      {durationText && <span>⏱ approx. {durationText}</span>}
                      {!km && !durationText && <span>Travel details will be updated</span>}
                    </div>
                  </div>
                </div>
              );
            }
          }

          return (
            <React.Fragment key={dayIndex}>
              {travelCard}
              <div className="timeline-day">

              <div className="day-marker-premium">{day.day}</div>
              <div className="day-content">
                <header className="day-header">
                  <h2 className="day-title">{dateDisplay}</h2>
                  <p className="day-subtitle">
                    {day.destinations.map(d => {
                      // First use the name from itinerary (Gemini provides this correctly)
                      // Fall back to database lookup if name is not in itinerary
                      if (d.name) {
                        return d.name;
                      }
                      const dest = destinationDetails.find(detailed => detailed._id === d.destinationId);
                      return dest ? dest.name : "Destination";
                    }).join(" • ")}
                  </p>
                </header>
                {day.dailySummary && (
                  <p className="day-summary-premium">
                    {day.dailySummary}
                  </p>
                )}

                {/* Activities Section */}
                <div className="day-section">
                  <div className="section-header-modern">
                    <span>✨ Things to Do</span>
                    <button className="btn-link" style={{ fontSize: "14px", color: "#1CA8CB", fontWeight: "500", background: "none", border: "none", cursor: "pointer" }} onClick={() => handleChangeClick('activity', day.destinations[0], day.day, 0)}>+ Add Activity</button>
                  </div>
                  <div className="activity-list">
                    {(() => {
                      // Collect all activities from all destinations, then de-duplicate by activityId
                      const allActivities = day.destinations.flatMap((dest, destIndex) =>
                        (dest.activities && Array.isArray(dest.activities) ? dest.activities : []).map((activity, activityIndex) => ({
                          activity,
                          destIndex,
                          activityIndex
                        }))
                      );

                      // De-duplicate by activityId - keep only first occurrence
                      const seenIds = new Set();
                      const uniqueActivities = allActivities.filter(item => {
                        if (seenIds.has(item.activity.activityId)) {
                          return false; // Skip duplicate
                        }
                        seenIds.add(item.activity.activityId);
                        return true;
                      });

                      return uniqueActivities.map(({ activity, destIndex, activityIndex }) => {
                        const details = activityDetails[activity.activityId] || {};
                        const name = details.name || activityNames[activity.activityId] || "Loading activity...";
                        const image = details.image;
                        const description = details.description;

                        return (
                          <div key={`${destIndex}-${activityIndex}-${activity.activityId}`} className="activity-card-premium">
                            {/* Image */}
                            <div className="activity-image-wrapper">
                              {image ? (
                                <img
                                  src={image}
                                  alt={name}
                                  onError={(e) => { e.target.style.display = "none"; }}
                                />
                              ) : (
                                <div className="activity-image-fallback">🎢</div>
                              )}
                            </div>

                            {/* Content */}
                            <div className="activity-details">
                              <h4 className="activity-title">
                                <span>{name}</span>
                              </h4>
                              {description && (
                                <p className="activity-description">
                                  {description}
                                </p>
                              )}
                            </div>

                            {/* Remove Button */}
                            <button
                              onClick={() => handleRemoveActivity(dayIndex, destIndex, activityIndex)}
                              className="btn-remove-subtle"
                              title="Remove Activity"
                            >
                              ×
                            </button>
                          </div>
                        );
                      });
                    })()}
                  </div>
                  {day.destinations.every(d => !d.activities || d.activities.length === 0) && (
                    <div style={{
                      padding: "12px",
                      backgroundColor: "#fef3c7",
                      borderRadius: "8px",
                      border: "1px dashed #f59e0b",
                      textAlign: "center"
                    }}>
                      <p style={{ fontSize: "14px", color: "#92400e", margin: "0" }}>
                        🎯 Activities coming soon for this destination. Click "+ Add Activity" to explore options.
                      </p>
                    </div>
                  )}
                </div>

                {/* Hotels Section */}
                <div className="day-section">
                  <div className="section-header-modern">
                    <span>🏨 Stay</span>
                    {day.hotels.length === 0 && day.day !== modifiedItinerary.itinerary.length && (
                      <button className="btn-link" style={{ fontSize: "14px", color: "#1CA8CB", fontWeight: "500", background: "none", border: "none", cursor: "pointer" }} onClick={() => handleChangeClick('hotel', null, day.day, null)}>+ Add Hotel</button>
                    )}
                  </div>
                  {day.hotels.map((hotel, hIdx) => {
                    const detail = hotelDetails?.find(h => h._id === hotel.hotelId);

                    // Calculate guest summary from packageDetails
                    const guestSummary = (() => {
                      if (!packageDetails?.guests || packageDetails.guests.length === 0) return null;
                      const totalAdults = packageDetails.guests.reduce((sum, g) => sum + (parseInt(g.adults, 10) || 0), 0);
                      const allChildren = packageDetails.guests.flatMap(g => g.children || []).filter(age => !isNaN(parseInt(age, 10)));
                      const totalChildren = allChildren.length;

                      let summary = `${totalAdults} Adult${totalAdults !== 1 ? 's' : ''}`;
                      if (totalChildren > 0) {
                        const ages = allChildren.map(age => parseInt(age, 10)).join(', ');
                        summary += `, ${totalChildren} Child${totalChildren !== 1 ? 'ren' : ''} (age${totalChildren !== 1 ? 's' : ''} ${ages})`;
                      }
                      return summary;
                    })();

                    // Get hotel-level images (from detail.images)
                    const hotelImages = (detail?.images || []).filter(img =>
                      img && typeof img === 'string' && img.trim().length > 0
                    ).slice(0, 5);

                    // Get room type images from seasonalRates based on selected room type
                    const roomTypeImages = (() => {
                      if (!detail?.seasonalRates) return [];

                      let fallbackImages = [];

                      // Search through all seasonal rates and their room rates
                      for (const seasonalRate of detail.seasonalRates) {
                        if (seasonalRate.rates && seasonalRate.rates.length > 0) {
                          for (const rate of seasonalRate.rates) {
                            // Get valid images from this rate
                            const rateImages = (rate.images || []).filter(img =>
                              img && typeof img === 'string' && img.trim().length > 0
                            );

                            // Store first available images as fallback
                            if (fallbackImages.length === 0 && rateImages.length > 0) {
                              fallbackImages = rateImages;
                            }

                            // Try to match by roomType or roomCategory
                            if (hotel.roomType && rateImages.length > 0) {
                              const searchType = hotel.roomType.toLowerCase();
                              const rateType = (rate.roomType || '').toLowerCase();
                              const rateCategory = (rate.roomCategory || '').toLowerCase();

                              if (rateType === searchType || rateCategory === searchType) {
                                return rateImages.slice(0, 5);
                              }
                            }
                          }
                        }
                      }

                      // No exact match found - return fallback images
                      return fallbackImages.slice(0, 5);
                    })();

                    // Get room rate price from seasonalRates
                    const roomRatePrice = (() => {
                      if (!detail?.seasonalRates || !hotel.roomType) return null;
                      for (const seasonalRate of detail.seasonalRates) {
                        if (seasonalRate.rates && seasonalRate.rates.length > 0) {
                          const matchingRate = seasonalRate.rates.find(r =>
                            (r.roomType && r.roomType.toLowerCase() === hotel.roomType.toLowerCase()) ||
                            (r.roomCategory && r.roomCategory.toLowerCase() === hotel.roomType.toLowerCase())
                          );
                          if (matchingRate?.baseRate) {
                            return matchingRate.baseRate;
                          }
                        }
                      }
                      return null;
                    })();

                    return (
                      <div key={hIdx} className="hotel-card-enhanced">
                        {/* Left: Media Column */}
                        <div className="hotel-media-column">
                          {hotelImages.length > 0 ? (
                            <>
                              <img
                                src={hotelImages[0]}
                                alt={detail?.name || 'Hotel'}
                                className="hotel-main-image"
                                onClick={() => { setLightboxImages(hotelImages); setLightboxIndex(0); setLightboxOpen(true); }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              {hotelImages.length > 1 && (
                                <div className="hotel-thumbnail-strip">
                                  {hotelImages.slice(1, 5).map((img, imgIdx) => (
                                    <img
                                      key={imgIdx + 1}
                                      src={img}
                                      alt={`Thumbnail ${imgIdx + 2}`}
                                      className="hotel-thumbnail"
                                      onClick={() => { setLightboxImages(hotelImages); setLightboxIndex(imgIdx + 1); setLightboxOpen(true); }}
                                      onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                  ))}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="hotel-main-image" style={{ background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <span style={{ fontSize: '32px' }}>🏨</span>
                            </div>
                          )}
                        </div>

                        {/* Right: Info Column */}
                        <div className="hotel-info-column">
                          {!isPlanUnlocked ? (
                            <>
                              <div className="hotel-header-row" style={{ marginBottom: "12px" }}>
                                <div className="hotel-title-area">
                                  <h4 className="hotel-name" style={{ fontSize: "18px", color: "#1e293b", fontWeight: "700" }}>
                                    {detail?.rating || "4"}★ {packageDetails?.couplePreferences?.tripType || "Comfort"} Stay in {detail?.state?.destination || "Destination"}
                                  </h4>
                                </div>
                              </div>
                              <div className="verified-badge" style={{ marginBottom: "8px" }}>
                                <span className="verified-icon">✓</span>
                                <span>Verified Partner Hotel</span>
                              </div>
                              {guestSummary && (
                                <p className="hotel-guests" style={{ margin: "8px 0", fontSize: "14px", color: "#475569" }}>
                                  ✔ {guestSummary} • Breakfast included
                                </p>
                              )}
                              {detail?.amenities?.length > 0 && (
                                <p className="hotel-amenities" style={{ margin: "8px 0", color: "#475569", fontSize: "14px" }}>
                                  ✔ {detail.amenities.slice(0, 3).join(" • ")}
                                </p>
                              )}
                              <div style={{ marginTop: "16px", padding: "12px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px dashed #cbd5e1" }}>
                                <p style={{ fontSize: "14px", color: "#334155", margin: 0, fontWeight: "500" }}>Exact hotel name and pricing will be shared in the next step.</p>
                              </div>
                              <p style={{ marginTop: "12px", fontSize: "13px", color: "#10b981", fontWeight: "500" }}>
                                Selected based on your {packageDetails?.couplePreferences?.tripType || "Comfort"} preference
                              </p>
                            </>
                          ) : (
                            <>
                              <div className="hotel-header-row">
                                <div className="hotel-title-area">
                                  <h4 className="hotel-name">
                                    <span className="unlocked">
                                      {detail ? detail.name : "Loading..."}
                                    </span>
                                    {detail?.isHoneymoonFavorite && (
                                      <span className="honeymoon-badge" style={{ marginLeft: "8px", fontSize: "12px", display: "inline-block" }}>Honeymoon Favorite</span>
                                    )}
                                  </h4>
                                  {/* Location */}
                                  {detail?.state?.destination && (
                                    <p className="hotel-location" style={{ margin: "4px 0 8px 0", fontSize: "14px", color: "#64748b" }}>
                                      📍 {detail.state.destination}{detail.state.name ? `, ${detail.state.name}` : ''}
                                    </p>
                                  )}
                                </div>

                                <div className="hotel-card-actions">
                                  <button className="btn-change" onClick={() => handleChangeClick("hotel", detail, day.day, hIdx)}>Change</button>
                                  <span className="star-badge">{detail?.rating || 0}★</span>
                                </div>
                              </div>

                              {/* Room Type with Price */}
                              <div className="room-type-price-row" style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", marginBottom: "12px" }}>
                                <p className="hotel-room-type" style={{ margin: 0, fontWeight: "500", color: "#334155" }}>🛏️ {hotel.roomType}</p>
                                {roomRatePrice && (
                                  <span style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "#1CA8CB",
                                    backgroundColor: "#e0f2fe",
                                    padding: "4px 10px",
                                    borderRadius: "15px"
                                  }}>
                                    ₹{Math.round(roomRatePrice).toLocaleString()}/night
                                  </span>
                                )}
                              </div>

                              {/* Verified Badge */}
                              <div className="verified-badge">
                                <span className="verified-icon">✓</span>
                                <span>Verified Partner Hotel</span>
                              </div>

                              {guestSummary && (
                                <p className="hotel-guests" style={{ marginTop: "8px", fontSize: "13px" }}>
                                  👥 {guestSummary}
                                </p>
                              )}

                              {/* Amenities */}
                              {detail?.amenities?.length > 0 && (
                                <p className="hotel-amenities" style={{ marginTop: "12px", borderTop: "1px solid #f1f5f9", paddingTop: "12px", color: "#64748b" }}>
                                  {detail.amenities.slice(0, 4).join(" • ")}
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {day.hotels.length === 0 && (
                    <div style={{
                      padding: "12px",
                      backgroundColor: "#f0f9ff",
                      borderRadius: "8px",
                      border: "1px dashed #1CA8CB",
                      textAlign: "center"
                    }}>
                      <p style={{ fontSize: "14px", color: "#0369a1", margin: "0 0 8px 0" }}>
                        {day.day === modifiedItinerary.itinerary.length
                          ? "🎒 Departure Day - No Stay Required"
                          : " Hotel details coming soon for this destination"}
                      </p>
                      {day.day !== modifiedItinerary.itinerary.length && (
                        <button
                          className="btn-link"
                          style={{ fontSize: "12px", color: "#007bff", background: "none", border: "none", cursor: "pointer" }}
                          onClick={() => handleChangeClick('hotel', null, day.day, null)}
                        >
                          + Select Hotel Manually
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Cabs Section */}
                <div className="day-section">
                  <div className="section-header-modern">
                    <span>🚗 Travel</span>
                  </div>
                  <p style={{ fontSize: "13px", color: "#1CA8CB", fontWeight: "500", margin: "0 0 12px 0", display: "flex", alignItems: "center", gap: "6px" }}>
                    Cab, driver & on-ground coordination handled by Trippro
                  </p>
                  {day.cabs && day.cabs.length > 0 ? (
                    day.cabs.map((cab, cIdx) => {
                      const detail = cabDetails?.find(c => c._id === cab.cabId);
                      // If we have cabId but no detail yet, it's still loading
                      if (!detail && cabDetails === null) {
                        return (
                          <div key={cIdx} className="card-item">
                            <h4 className="card-title">Loading cab details...</h4>
                            <p className="card-detail">Please wait</p>
                          </div>
                        );
                      }
                      // If cabDetails loaded but this specific cab not found - data issue
                      if (!detail) {
                        return (
                          <div key={cIdx} className="card-item" style={{ backgroundColor: "#fff9e6", borderLeft: "4px solid #faad14" }}>
                            <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                              <span style={{ fontSize: "20px" }}>⚠️</span>
                              <div>
                                <h4 className="card-title" style={{ color: "#d48806", marginBottom: "6px" }}>
                                  Cab Details Unavailable
                                </h4>
                                <p style={{ fontSize: "13px", color: "#665c00", margin: "0 0 10px 0" }}>
                                  The assigned cab is no longer available. Please select a different option.
                                </p>
                                <button
                                  className="btn-change"
                                  style={{ backgroundColor: "#faad14", borderColor: "#faad14" }}
                                  onClick={() => handleChangeClick("cab", null, day.day, cIdx)}
                                >
                                  Select Cab
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div key={cIdx} className="card-item">
                          <h4 className="card-title">
                            {isPlanUnlocked ? (
                              <span>{detail.name}</span>
                            ) : (
                              <span>Private cab with driver for all transfers</span>
                            )}
                          </h4>
                          {isPlanUnlocked && (
                            <p className="card-detail">{detail.type || "Transport"} • {(() => {
                              // Determine seater count based on cab type for consistency
                              const cabType = (detail.type || "").toLowerCase();
                              if (cabType.includes("sedan")) return 4;
                              if (cabType.includes("mpv")) return 6;
                              if (cabType.includes("suv")) return 6;
                              if (cabType.includes("traveller") || cabType.includes("tempo")) return 12;
                              // Fallback to database value or default based on type
                              return detail.seats || 4;
                            })()} Seater</p>
                          )}
                          <button className="btn-change" onClick={() => handleChangeClick("cab", detail, day.day, cIdx)}>Change</button>
                        </div>
                      );
                    })
                  ) : (
                    <div className="card-item" style={{ backgroundColor: "#fff9e6", borderLeft: "4px solid #faad14" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                        <span style={{ fontSize: "20px" }}>⚠️</span>
                        <div>
                          <h4 className="card-title" style={{ color: "#d48806", marginBottom: "6px" }}>
                            No Matching Cab Found
                          </h4>
                          <p style={{ fontSize: "13px", color: "#665c00", margin: "0 0 10px 0" }}>
                            Your preferred cab type is not available for this destination. Please select from available options.
                          </p>
                          <button
                            className="btn-change"
                            style={{ backgroundColor: "#faad14", borderColor: "#faad14" }}
                            onClick={() => handleChangeClick("cab", null, day.day, 0)}
                          >
                            Select Available Cab
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

              </div>
            </div>
            </React.Fragment>
          );
        })}
      </div>

      {renderCTABlock()}

      {/* Pricing & Booking Section */}
      <section className="booking-section" style={{
        marginTop: "40px",
        marginBottom: "40px",
        padding: "30px",
        background: "white",
        borderRadius: "20px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 10px 30px rgba(0,0,0,0.05)"
      }}>
        <h3 style={{ fontSize: "24px", color: "#004e61", marginBottom: "24px", fontFamily: '"Merriweather", serif' }}>
          Pricing & Booking
        </h3>

        <div className="trip-price-section" style={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap", marginBottom: "30px" }}>
          <div className="price-box">
            <span className="price-label">Total Package Cost</span>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
              <span className="price-value">
                ₹{discountApplied
                  ? Math.round(discountedTotal).toLocaleString()
                  : Math.round(breakdownData?.overall?.finalTotal || packageDetails?.totalPrice || 0).toLocaleString()}
              </span>
              {discountApplied && (
                <span style={{ textDecoration: 'line-through', color: '#999', fontSize: '18px' }}>
                  ₹{Math.round(breakdownData?.overall?.finalTotal || packageDetails?.totalPrice || 0).toLocaleString()}
                </span>
              )}
            </div>
            {discountApplied && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span className="price-discount">{discountPercentage}% Discount Applied!</span>
                <button
                  onClick={removePromoCode}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#ff4d4f',
                    fontSize: '13px',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    padding: 0
                  }}
                >
                  Remove
                </button>
              </div>
            )}
            <p style={{ fontSize: "11px", color: "#888", margin: "4px 0 0 0", fontWeight: "normal" }}>
              Prices may vary based on dates and availability.
            </p>
          </div>

          {isPlanUnlocked && (
            <button className="btn-primary" onClick={() => setIsBreakdownModalOpen(true)}>
              Price Breakdown
            </button>
          )}
          <p style={{
            fontSize: "13px",
            color: "#059669",
            margin: 0,
            display: "flex",
            alignItems: "center",
            gap: "6px"
          }}>
            ✅ <a href="/cancellation-refund" style={{ color: "#059669", textDecoration: "underline" }}>100% refund up to 7 days before travel</a> (excluding flights)
          </p>
        </div>

        {/* Promo Code Section */}
        <div className="promo-input-group" style={{ maxWidth: "400px" }}>
          <input
            type="text"
            className="promo-input"
            placeholder="Have a promo code?"
            value={promoCode}
            onChange={(e) => {
              setPromoCode(e.target.value);
              setPromoError('');
              setDiscountApplied(false);
              setDiscountAmount(0);
            }}
          />
          <button className="btn-action" style={{ backgroundColor: "#28a745", color: "white" }} onClick={applyPromoCode}>
            Apply
          </button>
        </div>
        {promoError && <p style={{ color: "red", fontSize: "13px", marginTop: "5px" }}>{promoError}</p>}

        <button
          className="btn-link"
          style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', marginTop: '10px', padding: 0 }}
          onClick={() => setShowPromocodes(!showPromocodes)}
        >
          {showPromocodes ? "Hide available coupons" : "View available coupons"}
        </button>

        {showPromocodes && (
          <div style={{ marginTop: "15px", display: "grid", gap: "10px", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
            {promocodes.map((promo, idx) => (
              <div key={idx} style={{ padding: "10px", border: "1px dashed #1CA8CB", borderRadius: "8px", background: "#f0f8ff" }}>
                <strong style={{ color: "#1CA8CB" }}>{promo.code}</strong>
                <p style={{ fontSize: "12px", margin: "5px 0" }}>{promo.discount}% Off</p>
                <button
                  style={{ fontSize: "11px", padding: "4px 8px", background: "#1CA8CB", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
                  onClick={() => {
                    setPromoCode(promo.code);
                    applyPromoCode(promo.code);
                  }}
                >
                  Use Code
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Contact Capture Prompt */}
        <div style={{
          marginTop: "30px",
          padding: "24px",
          background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
          borderRadius: "16px",
          border: "1px solid #bae6fd",
          textAlign: "center"
        }}>
          <p style={{ fontSize: "18px", fontWeight: "600", color: "#0369a1", marginBottom: "20px" }}>
            💡 Want to save this plan or talk to a Trippro advisor?
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", justifyContent: "center" }}>
            <button
              onClick={() => {
                import("react-ga4").then((ReactGA) => {
                  ReactGA.default.event({
                    category: "Planner",
                    action: "Save Plan",
                    label: `Package: ${itinerary?._id}`,
                  });
                });
                setContactAction('save');
                setIsContactCaptureOpen(true);
              }}
              style={{
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: "600",
                background: "white",
                color: "#1CA8CB",
                border: "2px solid #1CA8CB",
                borderRadius: "30px",
                cursor: "pointer",
                transition: "all 0.2s"
              }}
            >
              📧 Save & get on Email
            </button>
            <button
              onClick={() => { setContactAction('talk'); setIsContactCaptureOpen(true); }}
              style={{
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: "600",
                background: "white",
                color: "#059669",
                border: "2px solid #059669",
                borderRadius: "30px",
                cursor: "pointer",
                transition: "all 0.2s"
              }}
            >
              📞 Talk to advisor
            </button>
            <button
              onClick={() => {
                import("react-ga4").then((ReactGA) => {
                  ReactGA.default.event({
                    category: "Planner",
                    action: "Book Plan",
                    label: `Package: ${itinerary?._id}`,
                  });
                });
                setContactAction('book');
                setIsContactCaptureOpen(true);
              }}
              style={{
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: "600",
                background: "linear-gradient(135deg, #1CA8CB 0%, #004E61 100%)",
                color: "white",
                border: "none",
                borderRadius: "30px",
                cursor: "pointer",
                transition: "all 0.2s"
              }}
            >
              🚀 Proceed to booking
            </button>
          </div>

          {/* Human Support Section */}
          <div style={{
            marginTop: "24px",
            padding: "16px 20px",
            background: "rgba(255, 255, 255, 0.8)",
            borderRadius: "12px",
            textAlign: "left"
          }}>
            <p style={{ fontSize: "15px", fontWeight: "600", color: "#02495b", marginBottom: "10px" }}>
              🤝 Who supports you during the trip?
            </p>
            <p style={{ fontSize: "14px", color: "#444", lineHeight: "1.6", margin: 0 }}>
              Your trip is supported by Trippro's on-ground team.<br />
              You'll have one <strong>dedicated advisor</strong> + <strong>WhatsApp support</strong> during your journey.
            </p>
            <div style={{ marginTop: "12px", display: "flex", flexWrap: "wrap", gap: "16px", fontSize: "13px", color: "#666" }}>
              <span>👤 Advisor: Aditya Saini</span>
              <span>📞 On Call: <a href="tel:9211375008" style={{ color: "#1CA8CB", textDecoration: "none" }}>9211375008</a> (9 AM – 9 PM)</span>
              <span>💬 WhatsApp: <a href="https://wa.me/919211376008" target="_blank" rel="noopener noreferrer" style={{ color: "#25D366", textDecoration: "none" }}>9211376008</a> (24x7)</span>
            </div>
          </div>
        </div>
      </section>

      {/* Refine Your Trip Section - Only visible after clicking Customize */}
      {isCustomizeVisible && (
        <section className={`refine-section ${isSticky ? 'sticky' : ''}`}>
          <h3 className="refine-title">✨ Refine Your Trip</h3>
          <p style={{ fontSize: "14px", color: "#666", marginBottom: "20px" }}>
            Not exactly what you were looking for? Adjust your preferences below and we'll regenerate the perfect itinerary.
          </p>

          <div className="refine-grid">
            <div className="refine-item">
              <span className="refine-label">Trip Style</span>
              <div className="refine-chips">
                {[
                  { type: "Budget", rating: [3] },
                  { type: "Comfort", rating: [4] },
                  { type: "Luxury", rating: [5] }
                ].map(item => (
                  <button
                    key={item.type}
                    className={`refine-chip ${customTripType === item.type ? 'active' : ''}`}
                    onClick={() => {
                      setCustomTripType(item.type);
                      setCustomHotelRating(item.rating);
                    }}
                  >
                    {item.type}
                  </button>
                ))}
              </div>
            </div>

            <div className="refine-item">
              <span className="refine-label">Meal Plan</span>
              <div className="refine-chips">
                {[
                  { label: "B'fast Only", value: "CPAI" },
                  { label: "B'fast & Dinner", value: "MAPAI" },
                  { label: "All Meals", value: "APAI" }
                ].map(item => (
                  <button
                    key={item.value}
                    className={`refine-chip ${customMealPlan === item.value ? 'active' : ''}`}
                    onClick={() => setCustomMealPlan(item.value)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="refine-item">
              <span className="refine-label">Couple Prefs</span>
              <div className="refine-chips" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                <select className="refine-select" value={customIntent} onChange={e => setCustomIntent(e.target.value)}>
                  <option value="Relax & unwind">Relax & unwind</option>
                  <option value="Explore places">Explore places</option>
                  <option value="Mix of both">Mix of both</option>
                </select>
                <select className="refine-select" value={customTripPace} onChange={e => setCustomTripPace(e.target.value)}>
                  <option value="Chill (slow & relaxed)">Chill Pace</option>
                  <option value="Balanced (steady)">Balanced Pace</option>
                  <option value="Packed (see it all)">Packed Pace</option>
                </select>
                <select className="refine-select" value={customStayPriority} onChange={e => setCustomStayPriority(e.target.value)}>
                  <option value="Premium stays">Premium Stays</option>
                  <option value="Better locations">Top Locations</option>
                  <option value="Budget balance">Budget Balance</option>
                </select>
                <select className="refine-select" value={customGoal} onChange={e => setCustomGoal(e.target.value)}>
                  <option value="Peaceful time together">Peaceful</option>
                  <option value="Memorable experiences">Memorable</option>
                  <option value="Instagram-worthy moments">Insta-worthy</option>
                </select>
              </div>
            </div>

            <div className="refine-item">
              <span className="refine-label">People</span>
              <div className="refine-guests">
                <div className="guest-control">
                  <span className="guest-type">Adults</span>
                  <div className="guest-stepper">
                    <button onClick={() => setCustomAdults(Math.max(1, customAdults - 1))}>-</button>
                    <span className="guest-value">{customAdults}</span>
                    <button onClick={() => { if (customAdults < 8) setCustomAdults(customAdults + 1); }}>+</button>
                  </div>
                </div>
                <div className="guest-control">
                  <span className="guest-type">Children</span>
                  <div className="guest-stepper">
                    <button onClick={() => setCustomChildren(Math.max(0, customChildren - 1))}>-</button>
                    <span className="guest-value">{customChildren}</span>
                    <button onClick={() => setCustomChildren(customChildren + 1)}>+</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button
            className="btn-refresh"
            onClick={handleRefreshItinerary}
            disabled={isRefreshing}
          >
            {isRefreshing ? "Regenerating..." : "🔄 Refresh Itinerary"}
          </button>
        </section>
      )}

      {/* Modals - Keeping original modal structures but applying new CSS classes for overlay/content */}
      {
        isFormModalOpen && (
          <div className="modal-overlay" onClick={handleModalClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2 style={{ marginBottom: "20px" }}>Book Your Trip</h2>
              {['name', 'email', 'phone'].map((field) => (
                <div key={field} className="form-group">
                  <label className="form-label">{field.charAt(0).toUpperCase() + field.slice(1)}</label>
                  <input
                    className="form-input"
                    type={field === 'email' ? 'email' : 'text'}
                    name={field}
                    value={formData[field]}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                  />
                  {errors[field] && <p style={{ color: "red", fontSize: "12px" }}>{errors[field]}</p>}
                </div>
              ))}

              <div style={{ display: "flex", gap: "10px", marginTop: "30px" }}>
                <button className="btn-primary" style={{ flex: 1 }} onClick={handleSubmit} disabled={loadingSubmit}>
                  {loadingSubmit ? "Processing..." : "Confirm Booking"}
                </button>
                <button className="btn-nav" style={{ flex: 1 }} onClick={() => setFormModalOpen(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )
      }

      {
        isChangeModalOpen && (
          <div className="modal-overlay" onClick={handleModalClose}>
            <div className="modal-content" style={{ maxWidth: "600px", maxHeight: "80vh", overflow: "auto", position: "relative" }} onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setChangeModalOpen(false)}
                style={{
                  position: "absolute",
                  top: "15px",
                  right: "15px",
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#666",
                  padding: "0",
                  lineHeight: "1"
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = "#1CA8CB"}
                onMouseLeave={(e) => e.currentTarget.style.color = "#666"}
              >
                ✕
              </button>
              <h3 style={{ marginBottom: "20px", color: "#02495b", borderBottom: "2px solid #1CA8CB", paddingBottom: "10px", paddingRight: "30px" }}>
                Select Alternative {changeModalType === 'hotel' ? 'Hotel' : changeModalType === 'activity' ? 'Activity' : 'Cab'}
              </h3>
              {loadingAlternatives ? (
                <div className="spinner" style={{ width: "30px", height: "30px", borderWidth: "3px" }}></div>
              ) : (
                <div style={{ display: "grid", gap: "15px" }}>
                  {alternativeOptions.length > 0 ? alternativeOptions.map((opt, i) => {
                    const imageUrl = opt.image || (opt.images && opt.images.length > 0 ? opt.images[0] : null);

                    return (
                      <div key={i} style={{
                        display: "flex",
                        gap: "15px",
                        padding: "15px",
                        borderRadius: "12px",
                        border: "1px solid #e0e0e0",
                        background: "#fafafa",
                        transition: "all 0.2s",
                        cursor: "pointer"
                      }}
                        onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.1)"; e.currentTarget.style.borderColor = "#1CA8CB"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "#e0e0e0"; }}
                      >
                        {/* Image (Hotel or Activity) */}
                        <div style={{ flexShrink: 0 }}>
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={opt.name}
                              style={{
                                width: "100px",
                                height: "80px",
                                objectFit: "cover",
                                borderRadius: "8px",
                                border: "1px solid #ddd"
                              }}
                              onError={(e) => { e.target.style.display = "none"; }}
                            />
                          ) : (
                            <div style={{
                              width: "100px",
                              height: "80px",
                              backgroundColor: "#e9f6f9",
                              borderRadius: "8px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "24px",
                              color: "#1CA8CB",
                              fontWeight: "bold"
                            }}>
                              {changeModalType === 'activity' ? '🎢' : '🏨'}
                            </div>
                          )}
                        </div>

                        {/* Details */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "10px" }}>
                            <h4 style={{ margin: "0 0 6px 0", fontSize: "15px", color: "#02495b", fontWeight: "600" }}>
                              {opt.name}
                              {opt.isHoneymoonFavorite && (
                                <span className="honeymoon-badge" style={{ fontSize: "10px", padding: "2px 8px", marginLeft: "8px" }}>Honeymoon Favorite</span>
                              )}
                            </h4>
                            {opt.rating && (
                              <span style={{
                                fontSize: "11px",
                                backgroundColor: "#f97316",
                                color: "white",
                                padding: "2px 8px",
                                borderRadius: "12px",
                                fontWeight: "600",
                                flexShrink: 0
                              }}>
                                {opt.rating}★
                              </span>
                            )}
                          </div>

                          {/* Description for Activity */}
                          {changeModalType === 'activity' && opt.description && (
                            <div style={{ marginBottom: "8px" }}>
                              <p style={{
                                fontSize: "12px",
                                color: "#555",
                                margin: "0 0 2px 0",
                                lineHeight: "1.4",
                                overflow: "hidden",
                                display: "-webkit-box",
                                WebkitLineClamp: expandedActivities[opt._id] ? "unset" : 2,
                                WebkitBoxOrient: "vertical"
                              }}>
                                {opt.description}
                              </p>
                              {opt.description.length > 100 && (
                                <span
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedActivities(prev => ({
                                      ...prev,
                                      [opt._id]: !prev[opt._id]
                                    }));
                                  }}
                                  style={{
                                    color: "#1CA8CB",
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    cursor: "pointer"
                                  }}
                                >
                                  {expandedActivities[opt._id] ? "Read Less" : "...Read More"}
                                </span>
                              )}
                            </div>
                          )}

                          {/* Location (Hotel specific mainly) */}
                          {changeModalType !== 'activity' && opt.state?.destination && (
                            <p style={{ fontSize: "12px", color: "#666", margin: "0 0 4px 0" }}>
                              📍 {opt.state.destination}{opt.state.name ? `, ${opt.state.name}` : ""}
                            </p>
                          )}

                          {/* Room Types (Hotel specific) */}
                          {opt.seasonalRates && opt.seasonalRates[0]?.rates && (
                            <p style={{ fontSize: "11px", color: "#888", margin: "0 0 8px 0" }}>
                              🛏️ {opt.seasonalRates[0].rates[0]?.roomCategory || opt.seasonalRates[0].rates[0]?.roomType || "Room"}
                            </p>
                          )}

                          {/* Price and Action */}
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "auto" }}>
                            <p style={{
                              fontSize: "16px",
                              color: "#1CA8CB",
                              fontWeight: "700",
                              margin: 0
                            }}>
                              ₹{Math.round(opt.price || opt.rates || opt.pricePerDay || 0).toLocaleString()}
                              {changeModalType !== 'activity' && <span style={{ fontSize: "11px", fontWeight: "400", color: "#888" }}>/night</span>}
                            </p>
                            <button
                              className="btn-primary"
                              style={{ fontSize: "12px", padding: "8px 16px", borderRadius: "20px" }}
                              onClick={() => handleChangeSelection(opt._id)}
                            >
                              Select
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  }) : <p style={{ textAlign: "center", color: "#666" }}>No alternatives found for this location.</p>}
                </div>
              )}
              <button className="btn-nav" onClick={() => setChangeModalOpen(false)} style={{ marginTop: "20px", width: "100%", borderRadius: "25px" }}>Close</button>
            </div>
          </div>
        )
      }

      {
        isBreakdownModalOpen && breakdownData && (
          <div className="modal-overlay" onClick={handleModalClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2 style={{ marginBottom: "20px" }}>Price Breakdown</h2>
              {breakdownData.dayBreakdowns.map(day => (
                <div key={day.day} style={{ borderBottom: "1px solid #eee", paddingBottom: "10px", marginBottom: "10px" }}>
                  <strong>Day {day.day}</strong>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "14px" }}>
                    <span>Hotels</span><span>{day.hotelsTotal.toFixed(2)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "14px" }}>
                    <span>Cabs</span><span>{day.cabsTotal.toFixed(2)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "14px" }}>
                    <span>Activities</span><span>{day.activitiesTotal.toFixed(2)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "14px", fontWeight: "bold", marginTop: "5px" }}>
                    <span>Day Total</span><span>{day.dayBase.toFixed(2)}</span>
                  </div>
                </div>
              ))}
              <div style={{ background: "#f8f9fa", padding: "15px", borderRadius: "8px", marginTop: "20px" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Cost Price</span><span>{breakdownData.overall.base.toFixed(2)}</span>
                </div>
                {discountApplied && (
                  <div style={{ display: "flex", justifyContent: "space-between", color: "#28a745" }}>
                    <span>Discount ({discountPercentage}%)</span><span>-{discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    Service Charges
                    <span style={{ position: "relative", display: "inline-block" }}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "16px",
                          height: "16px",
                          borderRadius: "50%",
                          backgroundColor: "#1CA8CB",
                          color: "white",
                          fontSize: "10px",
                          fontWeight: "bold",
                          cursor: "help",
                          flexShrink: 0
                        }}
                        onMouseEnter={(e) => {
                          const tooltip = e.currentTarget.nextElementSibling;
                          if (tooltip) tooltip.style.visibility = "visible";
                          if (tooltip) tooltip.style.opacity = "1";
                        }}
                        onMouseLeave={(e) => {
                          const tooltip = e.currentTarget.nextElementSibling;
                          if (tooltip) tooltip.style.visibility = "hidden";
                          if (tooltip) tooltip.style.opacity = "0";
                        }}
                      >
                        i
                      </span>
                      <span
                        style={{
                          visibility: "hidden",
                          opacity: 0,
                          position: "absolute",
                          top: "50%",
                          left: "calc(100% + 8px)",
                          transform: "translateY(-50%)",
                          backgroundColor: "#333",
                          color: "#fff",
                          padding: "6px 10px",
                          borderRadius: "6px",
                          fontSize: "12px",
                          whiteSpace: "nowrap",
                          zIndex: 1000,
                          transition: "opacity 0.2s ease",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.2)"
                        }}
                      >
                        Applied 10% on Cost Price
                      </span>
                    </span>
                  </span>
                  <span>
                    {discountApplied
                      ? (breakdownData.overall.serviceCharge - discountAmount).toFixed(2)
                      : breakdownData.overall.serviceCharge.toFixed(2)}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    GST
                    <span style={{ position: "relative", display: "inline-block" }}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "16px",
                          height: "16px",
                          borderRadius: "50%",
                          backgroundColor: "#1CA8CB",
                          color: "white",
                          fontSize: "10px",
                          fontWeight: "bold",
                          cursor: "help",
                          flexShrink: 0
                        }}
                        onMouseEnter={(e) => {
                          const tooltip = e.currentTarget.nextElementSibling;
                          if (tooltip) tooltip.style.visibility = "visible";
                          if (tooltip) tooltip.style.opacity = "1";
                        }}
                        onMouseLeave={(e) => {
                          const tooltip = e.currentTarget.nextElementSibling;
                          if (tooltip) tooltip.style.visibility = "hidden";
                          if (tooltip) tooltip.style.opacity = "0";
                        }}
                      >
                        i
                      </span>
                      <span
                        style={{
                          visibility: "hidden",
                          opacity: 0,
                          position: "absolute",
                          top: "50%",
                          left: "calc(100% + 8px)",
                          transform: "translateY(-50%)",
                          backgroundColor: "#333",
                          color: "#fff",
                          padding: "6px 10px",
                          borderRadius: "6px",
                          fontSize: "12px",
                          whiteSpace: "nowrap",
                          zIndex: 1000,
                          transition: "opacity 0.2s ease",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.2)"
                        }}
                      >
                        {discountApplied
                          ? "Applied 18% on (Service Charges - Discount)"
                          : "Applied 18% on Service Charges"}
                      </span>
                    </span>
                  </span>
                  <span>
                    {discountApplied
                      ? ((breakdownData.overall.serviceCharge - discountAmount) * 0.18).toFixed(2)
                      : breakdownData.overall.gst.toFixed(2)}
                  </span>
                </div>
                <hr />
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "18px", fontWeight: "bold", color: "#1CA8CB" }}>
                  <span>Net Payable Amount</span>
                  <span>
                    {discountApplied
                      ? discountedTotal.toFixed(2)
                      : breakdownData.overall.finalTotal.toFixed(2)}
                  </span>
                </div>
                <p style={{ fontSize: "12px", color: "#888", marginTop: "12px", textAlign: "center", fontStyle: "italic" }}>
                  Prices are indicative. Final confirmation after trip advisor validation.
                </p>
              </div>
              <button className="btn-nav" onClick={() => setIsBreakdownModalOpen(false)} style={{ marginTop: "20px", width: "100%" }}>Close</button>
            </div>
          </div>
        )
      }

      {/* Contact Capture Modal */}
      {
        isContactCaptureOpen && (
          <div className="modal-overlay" onClick={() => setIsContactCaptureOpen(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2 style={{ marginBottom: "20px" }}>
                {contactAction === 'save' && 'Save Your Plan'}
                {contactAction === 'talk' && 'Talk to an Advisor'}
                {contactAction === 'book' && 'Proceed to Booking'}
              </h2>
              <p style={{ fontSize: "14px", color: "#666", marginBottom: "20px" }}>
                {contactAction === 'save' && 'We\'ll send this itinerary to your email.'}
                {contactAction === 'talk' && 'Our travel advisor will call you shortly.'}
                {contactAction === 'book' && 'Complete your details to proceed with booking.'}
              </p>
              {contactAction === 'talk' && (
                <div style={{
                  background: "#f0f9ff",
                  padding: "12px 16px",
                  borderRadius: "8px",
                  marginBottom: "20px",
                  fontSize: "13px",
                  color: "#02495b"
                }}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center" }}>
                    <span>👤 Advisor: <strong>Aditya Saini</strong></span>
                    <a href="tel:+919211375008" style={{ color: "#02495b", textDecoration: "none" }}>
                      📞 On Call: <strong style={{ textDecoration: "underline" }}>9211375008</strong> (9 AM – 9 PM)
                    </a>
                    <a
                      href="https://wa.me/919211376008?text=Hi%20Aditya!%20I%20need%20help%20with%20my%20travel%20plans."
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#02495b", textDecoration: "none" }}
                    >
                      💬 WhatsApp: <strong style={{ textDecoration: "underline" }}>9211376008</strong> (24x7)
                    </a>
                  </div>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Name</label>
                <input
                  className="form-input"
                  type="text"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Mobile</label>
                <input
                  className="form-input"
                  type="tel"
                  value={contactForm.mobile}
                  onChange={(e) => setContactForm({ ...contactForm, mobile: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  className="form-input"
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                />
              </div>
              <div style={{ display: "flex", gap: "10px", marginTop: "30px" }}>
                <button
                  className="btn-primary"
                  style={{ flex: 1 }}
                  disabled={contactSubmitting || !contactForm.name || !contactForm.mobile || !contactForm.email}
                  onClick={async () => {
                    setContactSubmitting(true);
                    try {
                      // Call leads API
                      const response = await fetch(`${API_BASE_URL}/leads`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          name: contactForm.name,
                          email: contactForm.email,
                          mobile: contactForm.mobile,
                          actionType: contactAction,
                          itineraryId: itinerary?._id || null,
                          itineraryTitle: itinerary?.title || itinerary?.packageDetails?.packageName || null,
                          itineraryData: modifiedItinerary || itinerary || null,
                          estimatedPrice: breakdownData?.overall?.finalTotal || null
                        })
                      });

                      // If 'save' or 'book' action, also send package details via email
                      if ((contactAction === 'save' || contactAction === 'book') && response.ok) {
                        const breakdown = await computeBreakdown();
                        const originalTotal = breakdown.overall.finalTotal;

                        const emailPayload = {
                          name: contactForm.name,
                          email: contactForm.email,
                          phone: contactForm.mobile,
                          selectedPackage: itinerary?._id || '',
                          totalPrice: originalTotal,
                          finalPrice: discountedTotal || originalTotal,
                          discountApplied: discountApplied,
                          discountPercentage: discountPercentage,
                        };

                        const bookingResponse = await fetch(`${API_BASE_URL}/send-package-details`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'X-Idempotency-Key': `${contactAction}-email-${contactForm.email}-${Date.now()}`
                          },
                          body: JSON.stringify(emailPayload)
                        });

                        // Save userId to localStorage for My Trips feature
                        if (bookingResponse.ok) {
                          try {
                            const bookingData = await bookingResponse.json();
                            if (bookingData.userId) {
                              localStorage.setItem('trippro_userId', bookingData.userId);
                            }
                            localStorage.setItem('trippro_userEmail', contactForm.email.toLowerCase().trim());
                          } catch (e) { /* ignore parse errors */ }
                        }
                      }

                      if (response.ok) {
                        if (contactAction === 'book') {
                          alert(`Thank you ${contactForm.name}! Your booking details have been sent to your email.`);
                          setIsContactCaptureOpen(false);
                          setContactForm({ name: '', mobile: '', email: '' });
                          navigate('/my-trips');
                        } else {
                          alert(`Thank you ${contactForm.name}! ${contactAction === 'save' ? 'Your plan details have been sent to your email.' :
                            'Our advisor will call you shortly.'
                          }`);
                          setIsContactCaptureOpen(false);
                          setContactForm({ name: '', mobile: '', email: '' });
                        }
                      } else {
                        alert('Something went wrong. Please try again.');
                      }
                    } catch (err) {
                      console.error('Error:', err);
                      alert('Something went wrong. Please try again.');
                    } finally {
                      setContactSubmitting(false);
                    }
                  }}
                >
                  {contactSubmitting ? 'Submitting...' : (
                    contactAction === 'save' ? 'Send to Email' :
                      contactAction === 'talk' ? 'Request Call' :
                        'Confirm Booking'
                  )}
                </button>
                <button className="btn-nav" style={{ flex: 1 }} onClick={() => setIsContactCaptureOpen(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )
      }

      {/* Feedback Prompt */}
      <div style={{
        textAlign: "center",
        padding: "30px 20px",
        marginTop: "40px",
        borderTop: "1px solid #e0e0e0"
      }}>
        <p style={{
          color: "#666",
          fontSize: "16px",
          margin: 0,
          fontFamily: "'Poppins', sans-serif"
        }}>
          Want to leave a feedback?{" "}
          <button
            onClick={() => showItineraryFeedback(true)}
            style={{
              background: "none",
              border: "none",
              color: "#1CA8CB",
              fontSize: "16px",
              cursor: "pointer",
              textDecoration: "underline",
              fontFamily: "'Poppins', sans-serif",
              padding: 0
            }}
          >
            Click here
          </button>
        </p>
      </div>

      {/* Unlock Plan Email Modal */}
      {showUnlockModal && (
        <div className="modal-overlay" onClick={() => setShowUnlockModal(false)}>
          <div className="modal-content unlock-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={() => setShowUnlockModal(false)}>×</button>
            <h3 className="modal-title">Unlock Full Detailed Plan</h3>
            <p className="modal-desc">
              Enter your email to instantly see exact hotel names, exact pricing, transfer details, and more. We will also email a copy of this plan to you.
            </p>
            <form onSubmit={handleUnlockSubmit} className="unlock-form">
              <input
                type="email"
                placeholder="Enter your email address"
                value={unlockEmail}
                onChange={(e) => setUnlockEmail(e.target.value)}
                required
                className="unlock-input"
              />
              <button type="submit" className="btn-primary unlock-submit-btn" disabled={unlockLoading}>
                {unlockLoading ? "Unlocking..." : "Show Full Plan"}
              </button>
            </form>
            <div className="trust-strip">
              <span className="trust-item"><span className="trust-icon">🔒</span> No spam, ever</span>
              <span className="trust-item"><span className="trust-icon">📧</span> Plan emailed to you</span>
              <span className="trust-item"><span className="trust-icon">⚡</span> Instant unlock</span>
            </div>
          </div>
        </div>
      )}

      {/* Image Lightbox Modal */}
      {
        lightboxOpen && (
          <div className="lightbox-overlay" onClick={() => setLightboxOpen(false)}>
            <button className="lightbox-close" onClick={() => setLightboxOpen(false)}>×</button>
            <button
              className="lightbox-nav lightbox-prev"
              onClick={(e) => { e.stopPropagation(); setLightboxIndex((prev) => (prev - 1 + lightboxImages.length) % lightboxImages.length); }}
            >
              ‹
            </button>
            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
              <img
                src={lightboxImages[lightboxIndex]}
                alt={`Image ${lightboxIndex + 1}`}
                className="lightbox-image"
              />
              <div className="lightbox-counter">{lightboxIndex + 1} / {lightboxImages.length}</div>
            </div>
            <button
              className="lightbox-nav lightbox-next"
              onClick={(e) => { e.stopPropagation(); setLightboxIndex((prev) => (prev + 1) % lightboxImages.length); }}
            >
              ›
            </button>
            <div className="lightbox-thumbnails" onClick={(e) => e.stopPropagation()}>
              {lightboxImages.map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt={`Thumbnail ${idx + 1}`}
                  className={`lightbox-thumb ${idx === lightboxIndex ? 'active' : ''}`}
                  onClick={() => setLightboxIndex(idx)}
                />
              ))}
            </div>
          </div>
        )
      }

      {/* Footer */}

      {/* Floating CTA Bar - appears when user scrolls past the Regret UX section */}
      {showFloatingCTA && !isPlanUnlocked && (
        <div className="floating-cta-bar">
          <button className="cta-unlock" onClick={() => setShowUnlockModal(true)}>
            🔓 View Exact hotels & pricing
          </button>
          <span className="cta-divider"></span>
          <button className="cta-customize" onClick={() => { setIsCustomizeVisible(true); document.querySelector('.refine-section')?.scrollIntoView({ behavior: 'smooth' }); }}>
            ✨ Customize
          </button>
          <span className="cta-divider"></span>
          <span className="cta-trust">🔒 Free • No spam</span>
        </div>
      )}

      {/* Unlock Success Toast */}
      {showUnlockToast && (
        <div className="unlock-toast">
          <span>✅</span> Plan unlocked! Full details revealed — check your email for a copy.
        </div>
      )}

      {/* Share With Partner Modal */}
      {showShareModal && (
        <div className="modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="modal-content share-modal-content" onClick={(e) => e.stopPropagation()} style={{ position: 'relative' }}>
            <button className="modal-close-btn" onClick={() => setShowShareModal(false)}>×</button>

            {shareSuccess ? (
              <div className="share-success">
                <span className="success-icon">💌</span>
                <h4>Plan sent successfully!</h4>
                <p>{shareForm.name ? `${shareForm.name} will` : "Your partner will"} receive the itinerary in their inbox shortly.</p>
                <button
                  className="btn-primary"
                  onClick={() => setShowShareModal(false)}
                  style={{ marginTop: '12px', padding: '12px 32px', borderRadius: '10px', background: 'linear-gradient(135deg, #1CA8CB 0%, #004E61 100%)', color: 'white', border: 'none', cursor: 'pointer', fontWeight: '600' }}
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                <h3 style={{ fontSize: '20px', color: '#004E61', fontFamily: '"Merriweather", serif', marginBottom: '6px' }}>
                  💌 Share with your travel partner
                </h3>
                <p style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.5' }}>
                  Planning together? Send this itinerary to your partner so you can decide together.
                </p>
                <form onSubmit={handleShareSubmit} className="share-form">
                  <label>Partner's Name (optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. Priya"
                    value={shareForm.name}
                    onChange={(e) => setShareForm(prev => ({ ...prev, name: e.target.value }))}
                  />
                  <label>Partner's Email *</label>
                  <input
                    type="email"
                    placeholder="partner@email.com"
                    value={shareForm.email}
                    onChange={(e) => setShareForm(prev => ({ ...prev, email: e.target.value }))}
                    required
                  />
                  <button type="submit" className="share-submit-btn" disabled={shareSubmitting}>
                    {shareSubmitting ? "Sending..." : "💌 Send Itinerary"}
                  </button>
                </form>
                <div className="trust-strip" style={{ marginTop: '12px' }}>
                  <span className="trust-item"><span className="trust-icon">🔒</span> We never spam</span>
                  <span className="trust-item"><span className="trust-icon">📧</span> One-time email only</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Unlock Email Modal */}
      {showUnlockModal && (
        <div className="modal-overlay" onClick={() => setShowUnlockModal(false)}>
          <div className="modal-content unlock-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={() => setShowUnlockModal(false)}>×</button>
            <h3 style={{ fontSize: '22px', color: '#004E61', fontFamily: '"Merriweather", serif', marginBottom: '8px' }}>
              Where should we send your detailed plan?
            </h3>
            <p style={{ fontSize: '15px', color: '#64748b', marginBottom: '10px' }}>
              Enter your email to unlock exact hotel names, precise pricing breakdowns, and to save this itinerary.
            </p>
            <form onSubmit={handleUnlockSubmit} className="unlock-form">
              <input
                type="email"
                placeholder="your.email@example.com"
                value={unlockEmail}
                onChange={(e) => setUnlockEmail(e.target.value)}
                required
                className="unlock-input"
              />
              <button type="submit" className="unlock-submit-btn" disabled={unlockLoading}>
                {unlockLoading ? "Unlocking..." : "🔓 Unlock Full Plan"}
              </button>
            </form>
            <div className="trust-strip">
              <span className="trust-item"><span className="trust-icon">🔒</span> 100% Free</span>
              <span className="trust-item"><span className="trust-icon">📧</span> No spam, ever</span>
            </div>
          </div>
        </div>
      )}

      <Footer />

    </div >
  );
}

export default AITripDetailPage;